import { isRepaymentSettled, repaymentStatusLabel } from "../../utils/repaymentStatus";
import { getMyInfoData } from "../../utils/myinfoData";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  InputNumber,
  message,
  Modal,
  Tabs,
  Timeline,
  Tooltip,
  Input,
  Steps,
  Upload,
} from "antd";
import { CircularProgress, useMediaQuery } from "@mui/material";
import Cookies from "js-cookie";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Typography, useTheme } from "@mui/material";
import * as Icons from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { tokens } from "../../theme";
import { format, subMonths } from "date-fns";
import {
  BulbOutlined,
  CloseOutlined,
  GlobalOutlined,
  LinkOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import pdfToText from "react-pdftotext";
import "./PersonalLoans.css";
import {
  createTrackingId,
  getDeviceType,
  trackUserProductInterest,
} from "../../utils/userProductInterest";
import {
  dismissOfferAlert,
  shouldShowOfferAlert,
} from "../../utils/offerAlert";
import OfferReadyModal from "../../components/OfferReadyModal";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
const { TabPane } = Tabs;

const BRAND = "var(--br-primary, rgb(14, 90, 165))";
const BRAND2 = "rgb(109, 92, 246)"; // accent similar to wizard
const PRIMARY_COLOR = "rgb(10, 61, 98)";
const MONEY_LENDER_CAMPAIGN_COOKIE = "money_lender_campaign_data";
const MONEY_LENDER_CAMPAIGN_DISMISSED_COOKIE =
  "money_lender_campaign_dismissed";
const MIN_PREFERRED_INTEREST_RATE = 2.1;
const MAX_PREFERRED_INTEREST_RATE = 4;
const ALLOWED_TENOR_MONTH_OPTIONS = [3, 6, 9, 12];
const DEFAULT_TENOR_MONTH = 3;
const FIXED_MONEY_LENDER_INTEREST_RATE_PM = 3.92;
const DEFAULT_PREFERRED_INTEREST_RATE = FIXED_MONEY_LENDER_INTEREST_RATE_PM;
const PROCESSING_FEE_RATE = 0.1;
const COMMITTED_EFFECTIVE_RATE_TOOLTIP =
  "Rates and fees are standardized and honoured. No lender can lure you with a low rate here and raise it at the branch. What you see is the lender's committed starting point — further negotiation is only ever in your favour, after their due diligence.";

const PRIMARY_BUTTON_STYLE = {
  background: PRIMARY_COLOR,
  borderColor: PRIMARY_COLOR,
  color: "#fff",
};
const LOAN_HUB_CARD_STYLE = {
  borderRadius: "var(--br-card-radius, 16px)",
  backgroundColor: "var(--br-surface, rgba(255,255,255,0.76))",
  padding: "16px",
  border: "1px solid rgba(148,163,184,0.18)",
  boxShadow: "var(--br-shadow, 0 12px 28px rgba(15,23,42,0.06))",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const LOAN_HUB_CARD_LAYOUT = {
  display: "flex",
  flexWrap: "wrap",
  gap: "15px",
};

const STATUS_COPY = {
  pending_borrower_action: { label: "Pending your response", color: "gold" },
  borrower_renegotiation: {
    label: "Waiting on lender revision",
    color: "purple",
  },
  borrower_rejected: { label: "Rejected", color: "red" },
  awaiting_lender_documents: {
    label: "Awaiting lender documents",
    color: "blue",
  },
  pending_borrower_signature: {
    label: "Documents ready for signature",
    color: "green",
  },
  pending_lender_confirmation: {
    label: "Awaiting lender confirmation",
    color: "cyan",
  },
  revision_requested: { label: "Changes requested by lender", color: "orange" },
  pending_disbursement: { label: "Pending disbursement", color: "geekblue" },
  ongoing: { label: "Ongoing", color: "green" },
  funded: { label: "Funded", color: "green" },
};

const HISTORY_COPY = {
  offline_approved: "Approved offline in CRM",
  offer_created: "Offer created",
  offer_updated: "Offer updated",
  accepted_offer: "Offer accepted",
  rejected_offer: "Offer rejected",
  requested_renegotiation: "Renegotiation requested",
  uploaded_initial_documents: "Lender uploaded documents",
  uploaded_signed_documents: "Signed documents uploaded",
  reuploaded_documents: "Updated documents uploaded",
  documents_confirmed: "Documents confirmed",
  revision_requested: "Lender requested document changes",
  uploaded_document: "Document uploaded",
  borrower_confirmed_documents: "You confirmed documents are ready",
};

const HISTORY_COLORS = {
  lender: "blue",
  borrower: "green",
  system: "gray",
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return numeric.toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
    minimumFractionDigits: 2,
  });
};

const formatRoundedCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return numeric.toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatPercent = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return `${numeric}% p.m.`;
};

const formatPercentPerMonth = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return `${numeric}% p.m.`;
};

const formatMonths = (value) => {
  if (!value) return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return `${numeric} month${numeric === 1 ? "" : "s"}`;
};

const normalizePreferredInterestRate = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_PREFERRED_INTEREST_RATE;
  return FIXED_MONEY_LENDER_INTEREST_RATE_PM;
};

const normalizePreferredTenorMonths = (value) => {
  const numeric = Number(value);
  return ALLOWED_TENOR_MONTH_OPTIONS.includes(numeric)
    ? numeric
    : DEFAULT_TENOR_MONTH;
};

const calculateMonthlyInstallment = (amount, ratePm, tenorMonths) => {
  const principal = Number(amount || 0);
  const monthlyRate = Number(ratePm || 0) / 100;
  const months = Number(tenorMonths || 0);
  if (!principal || !months) return null;
  if (monthlyRate === 0) return principal / months;
  const rateFactor = Math.pow(1 + monthlyRate, -months);
  return principal * (monthlyRate / (1 - rateFactor));
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isLoanExpired = (loan) => {
  if (!loan?.created_at) return false;
  const createdAt = new Date(loan.created_at);
  if (Number.isNaN(createdAt.getTime())) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  return createdAt < cutoff;
};

const getStatusDisplay = (status) => {
  if (!status) return { label: "—", color: "default" };
  return (
    STATUS_COPY[status] || {
      label: status.replace(/_/g, " "),
      color: "default",
    }
  );
};

const getInitials = (name) => {
  const textToSplit = name?.split(" ") || [];
  const initials =
    textToSplit?.[0]?.[0]?.toUpperCase() +
    (textToSplit?.[1]?.[0]?.toUpperCase() || "");
  return initials || "";
};

const extractTextBetween = (text, beforeText, afterText) => {
  if (!text) return "";
  const beforeTextPattern = beforeText.replace(/\s+/g, "\\s+");
  const afterTextPattern = afterText.replace(/\s+/g, "\\s+");
  const regex = new RegExp(`${beforeTextPattern}(.*?)${afterTextPattern}`);
  const match = text.match(regex);
  return match && match[1] ? match[1].trim() : "";
};

const parseCbsData = (text) => {
  if (!text) return null;
  const securedCreditLimit = extractTextBetween(
    text,
    "Secured Credit Limit",
    "Unsecured Credit Limit",
  );
  const unsecuredCreditLimit = extractTextBetween(
    text,
    "Unsecured Credit Limit",
    "Exempted Credit Limit",
  );
  const creditScore = extractTextBetween(
    text,
    "financial institution.  Score",
    "Risk Grade",
  );
  const riskGrade = extractTextBetween(
    text,
    "Risk Grade ",
    "Risk Grade Description",
  );
  const probabilityDefault = extractTextBetween(
    text,
    "Probability of Default ",
    " For more details on the description",
  );
  return {
    secured_credit_limit: securedCreditLimit || null,
    unsecured_credit_limit: unsecuredCreditLimit || null,
    credit_score: creditScore || null,
    risk_grade: riskGrade || null,
    probability_default: probabilityDefault || null,
  };
};

const extractCbsEnquiryDate = (text) => {
  if (!text) return null;
  const enquirySegmentMatch = text.match(/Enquiry\s+Date\s*(.*?)\s*Reference/i);
  if (!enquirySegmentMatch?.[1]) return null;
  const dateMatch = enquirySegmentMatch[1].match(/\b\d{2}\/\d{2}\/\d{4}\b/);
  return dateMatch?.[0] || null;
};

const isCbsEnquiryDateValid = (dateString) => {
  if (!dateString) return false;
  const [day, month, year] = dateString.split("/").map(Number);
  if (!day || !month || !year) return false;
  const enquiryDate = new Date(year, month - 1, day);
  if (Number.isNaN(enquiryDate.getTime())) return false;

  const today = new Date();
  const cutoffDate = subMonths(today, 1);
  return enquiryDate >= cutoffDate && enquiryDate <= today;
};

const PersonalLoans = () => {
  const history = useHistory();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isLargeScreen = useMediaQuery("(min-width:1200px)");
  const isTablet = useMediaQuery("(max-width:1099px)");
  const isMobile = useMediaQuery("(max-width:600px)");
  const trackingRef = useRef({
    trackingId: createTrackingId(),
    enteredLoanAmount: false,
    enteredInterestRate: false,
    enteredTenor: false,
    loanAmount: null,
  });
  const sendTrackingUpdate = (updates = {}) => {
    trackingRef.current = { ...trackingRef.current, ...updates };
    trackUserProductInterest({
      trackingId: trackingRef.current.trackingId,
      productPage: "b-personal-loans",
      deviceType: getDeviceType(isMobile),
      enteredLoanAmount: trackingRef.current.enteredLoanAmount,
      enteredInterestRate: trackingRef.current.enteredInterestRate,
      enteredTenor: trackingRef.current.enteredTenor,
      loanAmount: trackingRef.current.loanAmount,
    });
  };
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);

  useEffect(() => {
    sendTrackingUpdate();
  }, []);
  const [applyBusy, setApplyBusy] = useState(false);
  const [myLoans, setMyLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(true);
  const [applyForm, setApplyForm] = useState({
    amount: 10000,
    desired_rate_pa: DEFAULT_PREFERRED_INTEREST_RATE,
    desired_tenor_months: DEFAULT_TENOR_MONTH,
  });
  const [campaignAmountAutofilled, setCampaignAmountAutofilled] =
    useState(false);

  useEffect(() => {
    const campaignCookie = Cookies.get(MONEY_LENDER_CAMPAIGN_COOKIE);

    if (campaignCookie) {
      try {
        const campaign = JSON.parse(campaignCookie);
        const eligibleLoanAmount = Number(campaign?.eligible_loan_amount);
        const requestedLoanAmount = Number(campaign?.loan_amount);
        const loanTerm = Number(campaign?.loan_term);
        const prefilledAmount =
          Number.isFinite(eligibleLoanAmount) && eligibleLoanAmount > 0
            ? eligibleLoanAmount
            : Number.isFinite(requestedLoanAmount) && requestedLoanAmount > 0
              ? requestedLoanAmount
              : null;

        setApplyForm((prev) => ({
          ...prev,
          amount: prefilledAmount ?? prev.amount,
          desired_rate_pa: DEFAULT_PREFERRED_INTEREST_RATE,
          desired_tenor_months: Number.isFinite(loanTerm)
            ? normalizePreferredTenorMonths(loanTerm)
            : prev.desired_tenor_months,
        }));
        setCampaignAmountAutofilled(Boolean(prefilledAmount));
      } catch (error) {
        console.error("Unable to parse money lender campaign cookie", error);
      }
    }

    Cookies.set(MONEY_LENDER_CAMPAIGN_DISMISSED_COOKIE, "true", {
      path: "/",
      expires: 30,
    });
    Cookies.remove(MONEY_LENDER_CAMPAIGN_COOKIE, { path: "/" });
    Cookies.remove("sync_source", { path: "/" });
    sessionStorage.removeItem("showCampaignPersonalLoanPrompt");
  }, []);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyStep, setApplyStep] = useState(0);
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [userDetails, setUserDetails] = useState(null);
  const [payslipFiles, setPayslipFiles] = useState([]);
  const [loanAmountExceeded, setLoanAmountExceeded] = useState(false);
  const [cbsPromoCode, setCbsPromoCode] = useState("");
  const [cbsFiles, setCbsFiles] = useState([]);
  const [cbsFileText, setCbsFileText] = useState("");
  const [cbsExtractedData, setCbsExtractedData] = useState(null);
  const [cbsEnquiryDate, setCbsEnquiryDate] = useState(null);
  const [authorizationChecked, setAuthorizationChecked] = useState(false);
  const [allowLenderVisibility, setAllowLenderVisibility] = useState(true);
  const [visibilityUpdating, setVisibilityUpdating] = useState({});
  const [offerAction, setOfferAction] = useState({
    open: false,
    action: null,
    offer: null,
  });
  const [offerMessage, setOfferMessage] = useState("");
  const [acceptOfferAmount, setAcceptOfferAmount] = useState(null);
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [uploadingOfferId, setUploadingOfferId] = useState(null);
  const [confirmingOfferId, setConfirmingOfferId] = useState(null);
  const [uploadNotesMap, setUploadNotesMap] = useState({});
  const [supportingUploadBusy, setSupportingUploadBusy] = useState({});
  const [mlcbModalOpen, setMlcbModalOpen] = useState(false);
  const [mlcbFiles, setMlcbFiles] = useState([]);
  const [mlcbUploadBusy, setMlcbUploadBusy] = useState(false);
  const [lenderProfileData, setLenderProfileData] = useState(null);
  const [repaymentScheduleModal, setRepaymentScheduleModal] = useState({
    open: false,
    offer: null,
  });
  const [statementCoverageMap, setStatementCoverageMap] = useState({});
  const [statementCoverageLoading, setStatementCoverageLoading] = useState({});
  const [statementCoverageModal, setStatementCoverageModal] = useState({
    open: false,
    loan: null,
    missingMonths: [],
  });
  const [statementCoverageUploads, setStatementCoverageUploads] = useState({});
  const [statementCoverageSubmitting, setStatementCoverageSubmitting] =
    useState(false);
  const fileInputRefs = useRef({});
  const supportingFileRefs = useRef({});
  const [activeTab, setActiveTab] = useState("apply");
  const [offerAlert, setOfferAlert] = useState({ open: false, data: null });
  const [displayAds, setDisplayAds] = useState([]);
  const [adImage, setAdImage] = useState(null);
  const [adLink, setAdLink] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const iconLibrary = Icons;
  const ongoingStatuses = useMemo(
    () => new Set(["ongoing", "pending_disbursement", "funded"]),
    [],
  );

  const CATS = useMemo(
    () => [
      { key: "all", label: "All" },
      { key: "smartlend", label: "About SmartLend" },
      { key: "borrowing", label: "Borrowing Rules" },
      { key: "safety", label: "Safety & Scams" },
      { key: "rates", label: "Rates" },
    ],
    [],
  );

  const FAQS = useMemo(
    () => [
      {
        cat: "borrowing",
        q: "How much can I borrow? What are the legal limits in Singapore?",
        a: (
          <>
            <div className="faq-highlight">
              Your limit depends on your annual income — and it's a combined cap
              across all moneylenders.
            </div>

            <div className="compare-table">
              <div className="compare-row header">
                <div>Annual Income</div>
                <div>Citizens &amp; PRs</div>
                <div>Foreigners in SG</div>
              </div>
              <div className="compare-row">
                <div>Below S$10,000</div>
                <div>S$3,000</div>
                <div>S$500</div>
              </div>
              <div className="compare-row">
                <div>S$10,000 – S$19,999</div>
                <div>S$3,000</div>
                <div>S$3,000</div>
              </div>
              <div className="compare-row">
                <div>S$20,000 and above</div>
                <div>6× monthly income</div>
                <div>6× monthly income</div>
              </div>
            </div>

            <p style={{ marginTop: 12 }}>
              <strong>Important:</strong> These are aggregate limits — your
              total outstanding borrowing across all licensed moneylenders
              cannot exceed this. SmartLend&apos;s lenders check your MLCB
              record before making an offer, so the amount you&apos;re offered
              already factors in any existing loans.
            </p>

            <p style={{ marginTop: 8, fontSize: 12, color: "#7a8499" }}>
              Source: Ministry of Law Singapore —{" "}
              <a
                href="https://rom.mlaw.gov.sg/information-for-borrowers/guide-to-borrowing-from-licensed-moneylenders-english/"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#1a3cbe" }}
              >
                rom.mlaw.gov.sg
              </a>
            </p>
          </>
        ),
      },

      {
        cat: "borrowing",
        q: "What's the maximum interest rate? Are there other fees I should know about?",
        a: (
          <>
            <div className="faq-highlight">
              <span style={{ fontWeight: 800 }}>By law: max</span>
              <span style={{ fontWeight: 300 }}>
                {" "}
                4% per month interest · S$60/month late fee · 10% one-time admin
                fee on principal.
              </span>
            </div>

            <ul>
              <li>
                💰 <strong>Interest rate:</strong> Max 4% per month on
                outstanding balance (reducing basis). Most reputable lenders
                charge 1–3%.
              </li>
              <li>
                💰 <strong>Admin fee:</strong> One-time, up to 10% of principal,
                deducted upfront. On S$10,000 loan = max S$1,000.
              </li>
              <li>
                💰 <strong>Late repayment interest:</strong> Max 4% per month —
                but only on the overdue amount, not the full loan.
              </li>
              <li>
                💰 <strong>Late repayment fee:</strong> Max S$60 per month, per
                missed instalment.
              </li>
              <li>
                💰 <strong>Legal fees:</strong> Only if lender takes court
                action for a defaulted loan — must be court-ordered.
              </li>
              <li>
                🛡️ <strong>Total charges cap:</strong> All interest, late
                interest, admin fee and late fees combined{" "}
                <strong>cannot exceed the original loan principal</strong>. E.g.
                on a S$10,000 loan, maximum total charges are capped at S$10,000
                — so you will never repay more than S$20,000 in total.
              </li>
            </ul>

            <p style={{ marginTop: 10 }}>
              Any lender charging beyond these is acting illegally. Report them
              to the Registry of Moneylenders at <strong>1800-2255-529</strong>.
              All SmartLend partner lenders are audited against these legal
              limits.
            </p>

            <p style={{ marginTop: 8 }}>
              <strong>Watch out for early repayment penalties</strong> — some
              lenders charge a fee if you repay ahead of schedule. Always check
              your contract before signing.
            </p>
          </>
        ),
      },

      {
        cat: "borrowing",
        q: "Will borrowing from a moneylender affect my credit score or bank loan eligibility?",
        a: (
          <>
            <div className="faq-highlight">
              Licensed moneylenders report to MLCB — not CBS. Your bank credit
              score is generally unaffected.
            </div>

            <ul>
              <li>
                🏦 <strong>Credit Bureau Singapore (CBS)</strong> is used by
                banks — licensed moneylenders don't report to CBS, so taking a
                moneylender loan{" "}
                <strong>won't affect your bank loan applications.</strong>{" "}
                <br />
                <strong>Note:</strong> Some banks may ask for your MLCB report
                if you're the sole director or shareholder of a company.
              </li>
              <li>
                📋 <strong>Moneylenders Credit Bureau (MLCB)</strong> — used
                only by licensed moneylenders. All borrowings, repayments and
                defaults are recorded here.
              </li>
              <li>
                ✅ Taking a moneylender loan{" "}
                <strong>will not lower your CBS score</strong>
              </li>
              <li>
                ⚠️ Missing repayments{" "}
                <strong>will appear on your MLCB record</strong>, making future
                moneylender loans harder to get
              </li>
              <li>
                ✅ Responsible repayment can{" "}
                <strong>build a positive MLCB record</strong> over time
              </li>
            </ul>
          </>
        ),
      },

      {
        cat: "safety",
        q: "Is it safe? How do I tell a licensed moneylender from a loan shark?",
        a: (
          <>
            <div className="faq-highlight">
              It&apos;s completely safe — as long as you use a Ministry of
              Law-licensed lender. Here&apos;s how to tell them apart.
            </div>

            <div className="compare-table">
              <div className="compare-row header">
                <div></div>
                <div className="col-sl">Licensed Moneylender</div>
                <div className="col-br">Loan Shark</div>
              </div>
              <div className="compare-row">
                <div>Contacts you first?</div>
                <div className="col-sl">❌ Never (illegal)</div>
                <div className="col-br">✅ SMS/WhatsApp/Telegram</div>
              </div>
              <div className="compare-row">
                <div>Loan contract</div>
                <div className="col-sl">✅ Required by law</div>
                <div className="col-br">❌ None</div>
              </div>
              <div className="compare-row">
                <div>In-person meeting</div>
                <div className="col-sl">✅ Required before disbursement</div>
                <div className="col-br">❌ Fully remote</div>
              </div>
              <div className="compare-row">
                <div>Upfront fee to approve</div>
                <div className="col-sl">❌ Illegal</div>
                <div className="col-br">✅ Common scam</div>
              </div>
              <div className="compare-row">
                <div>Takes your NRIC/ATM card</div>
                <div className="col-sl">❌ Illegal</div>
                <div className="col-br">✅ Common</div>
              </div>
              <div className="compare-row">
                <div>Harassment on default</div>
                <div className="col-sl">❌ Illegal — report to MinLaw</div>
                <div className="col-br">✅ Frequent</div>
              </div>
            </div>

            <p style={{ marginTop: 12 }}>
              All SmartLend lenders are verified against MinLaw&apos;s Registry.
              Cross-check any lender at{" "}
              <a
                href="https://rom.mlaw.gov.sg"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#1a3cbe" }}
              >
                rom.mlaw.gov.sg
              </a>
              . Report harassment to the X-Ah Long hotline:{" "}
              <strong>1800-924-5664</strong>.
            </p>
          </>
        ),
      },

      {
        cat: "borrowing",
        q: "Can I get approved with bad credit, as a self-employed person, or as a foreigner?",
        a: (
          <>
            <div className="faq-highlight">
              Yes — moneylenders are far more flexible than banks. Your ability
              to repay matters more than credit history.
            </div>

            <ul>
              <li>
                ✅ <strong>Bad or no credit history</strong> — assessed
                individually, not auto-rejected by algorithm. Lenders focus on
                current income.
              </li>
              <li>
                ✅ <strong>Self-employed / freelancers</strong> — bank
                statements (3–6 months), CPF history, or IRAS NOA accepted as
                income proof
              </li>
              <li>
                ✅ <strong>Commission-based earners</strong> — last 3
                months&apos; payslips or CPF history accepted
              </li>
              <li>
                ✅ <strong>Foreigners on valid work passes</strong> — EP, S
                Pass, Work Permit holders eligible with income proof and valid
                passport
              </li>

              <li
                style={{
                  background: "#fff8ee",
                  border: "1px solid #ffd27a",
                  borderRadius: "var(--br-card-radius, 8px)",
                  padding: "10px 12px",
                  marginTop: 2,
                  listStyle: "none",
                }}
              >
                ⚠️ <strong>Note on foreigner quotas:</strong> Most licensed
                moneylenders have an annual quota on how many foreign nationals
                they can disburse loans to. Depending on the lender, they may
                reject your application or offer a lower loan amount — even if
                you meet all the eligibility criteria. SmartLend matches you to
                lenders who still have available quota, saving you the wasted
                trips.
              </li>

              <li>
                ⚠️ <strong>Unemployed</strong> — some lenders accept consistent
                alternative income (rental, dividends, alimony). Case-by-case.
              </li>
            </ul>

            <p style={{ marginTop: 10 }}>
              SmartLend matches you to lenders with the highest approval rates
              for your specific income type and profile — so you don&apos;t
              waste time or trips on lenders unlikely to approve you.
            </p>
          </>
        ),
      },

      {
        cat: "smartlend",
        q: "Why should I use SmartLend instead of going directly to a moneylender?",
        a: (
          <>
            <div className="faq-highlight">
              You save time, get better offers, and avoid the guesswork — all
              for free.
            </div>

            <p>
              Walking into a moneylender&apos;s office without doing your
              homework puts you at a disadvantage. You have no reference point
              for what&apos;s a fair rate, and you&apos;re negotiating alone.
              SmartLend changes that dynamic entirely.
            </p>

            <ul>
              <li>
                ✅ <strong>One application, multiple offers</strong> — instead
                of visiting multiple offices separately, you get competitive
                offers from our curated lenders sent to you
              </li>
              <li>
                ✅ <strong>Pre-vetted lenders</strong> — we only work with
                MAS-licensed lenders with proven track records and high approval
                rates
              </li>
              <li>
                ✅ <strong>90% offer consistency</strong> — 9 in 10 SmartLend
                users receive the same offer at the branch as shown online. No
                surprises when you show up.
              </li>
              <li>
                ✅ <strong>No wasted trips</strong> — know your odds before
                making the journey in person
              </li>
              <li>
                ✅ <strong>Zero cost to you</strong> — our lenders pay us, not
                you
              </li>
            </ul>

            <p style={{ marginTop: 10 }}>
              Think of SmartLend as having a knowledgeable friend who&apos;s
              already done the comparison shopping for you.
            </p>
          </>
        ),
      },

      {
        cat: "smartlend",
        q: "Is my personal data safe on SmartLend? Who sees my application?",
        a: (
          <>
            <div className="faq-highlight">
              Your data is encrypted and only shared with lenders you are
              matched to.
            </div>

            <ul>
              <li>
                ✅ All data is <strong>SSL-encrypted</strong> in transit and
                stored securely
              </li>
              <li>
                ✅ Your information is only shared with lenders{" "}
                <strong>after you submit a request</strong> — not before
              </li>
              <li>
                ✅ We do <strong>not</strong> sell or share your data with
                third-party marketers
              </li>
              <li>
                ✅ Our lenders are contractually prohibited from cold-calling or
                harassing you
              </li>
              <li>
                ✅ You can request deletion of your data at any time by
                contacting support
              </li>
            </ul>

            <p style={{ marginTop: 10 }}>
              If you use MyInfo via Singpass, your data is retrieved directly
              from government sources — it does not pass through
              SmartLend&apos;s servers.
            </p>

            <div
              style={{
                background: "#f5f8ff",
                border: "1.5px solid #c5d8ff",
                borderRadius: "var(--br-card-radius, 10px)",
                padding: "12px 16px",
                marginTop: 12,
                fontSize: 13,
                color: "#1a2236",
                lineHeight: 1.6,
              }}
            >
              <strong>Don&apos;t have Singpass?</strong> No worries. If
              you&apos;re having trouble signing up or you&apos;re a foreigner
              without Singpass access, simply WhatsApp us and our team will
              assist you manually.
              <br />
              <a
                href="https://wa.me/6587853373"
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  marginTop: 10,
                  background: "#25D366",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  padding: "9px 18px",
                  borderRadius: 50,
                  textDecoration: "none",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.139.564 4.144 1.546 5.877L0 24l6.293-1.521A11.934 11.934 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.896 0-3.67-.5-5.2-1.373l-.374-.217-3.876.937.979-3.77-.243-.389A9.953 9.953 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                </svg>
                WhatsApp +65 8785 3373
              </a>
            </div>
          </>
        ),
      },

      {
        cat: "smartlend",
        q: "Does SmartLend charge a fee? How is it different from a loan broker?",
        a: (
          <>
            <div className="faq-highlight">
              SmartLend is 100% free for borrowers — always.
            </div>

            <p>
              We don&apos;t charge a platform fee, referral fee, or any hidden
              charges at any point. Our mission is to lower the cost of
              borrowing for Singaporeans, and that starts before you even sign a
              contract.
            </p>

            <p style={{ marginTop: 12 }}>
              Here&apos;s how that stacks up against a traditional loan broker:
            </p>

            <div className="compare-table">
              <div className="compare-row header">
                <div></div>
                <div className="col-sl">SmartLend</div>
                <div className="col-br">Loan Broker</div>
              </div>
              <div className="compare-row">
                <div>Platform / Broker fee</div>
                <div className="col-sl">✅ Free — always</div>
                <div className="col-br">❌ 5–15% of loan</div>
              </div>
              <div className="compare-row">
                <div>Lender choice</div>
                <div className="col-sl">✅ Curated exclusive lenders</div>
                <div className="col-br">❌ Often just 1–2</div>
              </div>
              <div className="compare-row">
                <div>Transparency</div>
                <div className="col-sl">✅ Full offer details upfront</div>
                <div className="col-br">❌ May steer to preferred lender</div>
              </div>
              <div className="compare-row">
                <div>Pressure tactics</div>
                <div className="col-sl">✅ None — you choose</div>
                <div className="col-br">❌ Commission-driven</div>
              </div>
              <div className="compare-row">
                <div>Concierge support</div>
                <div className="col-sl">
                  ✅ Dedicated loan concierge to guide you
                </div>
                <div className="col-br">❌ Transactional only</div>
              </div>
            </div>

            <p style={{ marginTop: 12 }}>
              With SmartLend, lenders compete for your request. You compare
              offers side by side and choose on your own terms — no middleman,
              no markup.
            </p>

            {/* Optional: keep these blocks if your page styling supports them */}
            <div className="concierge-intro">
              <p className="concierge-intro-q">
                But what if you want a dedicated Relationship Manager (RM) to
                guide you every step of the way?
              </p>
              <p className="concierge-intro-body">
                Some of us prefer a human touch — and that&apos;s completely
                valid. For clients who&apos;d rather sit back and let the
                experts handle everything, SmartLend offers a full white-glove
                concierge service. Your dedicated RM takes care of all the
                negotiations, paperwork coordination, and lender communication
                on your behalf. You just tell us what you need, and we do the
                rest — so you walk away with the loan amount you need,
                stress-free.
              </p>
            </div>

            <div className="concierge-block">
              <div className="concierge-header">
                <div className="concierge-header-icon">✦</div>
                <div className="concierge-title">
                  Upgrade to Concierge — Your Fast-Track to Funding
                </div>
                <div className="concierge-sub">
                  Enjoy one-on-one RM support, pre-approval checks, and direct
                  lender negotiations — all handled for you.
                </div>
              </div>

              <div className="concierge-grid">
                <div className="concierge-feat">
                  <div className="cfeat-icon">👤</div>
                  <div>
                    <div className="cfeat-title">
                      Dedicated RM &amp; Unified Inbox
                    </div>
                    <div className="cfeat-desc">
                      We liaise with lenders on your behalf so you can focus on
                      your life and await approval stress-free.
                    </div>
                  </div>
                </div>

                <div className="concierge-feat">
                  <div className="cfeat-icon">✓</div>
                  <div>
                    <div className="cfeat-title">
                      Pre-Qualified Loan Matches
                    </div>
                    <div className="cfeat-desc">
                      Say goodbye to generic rejections. We connect you only
                      with lenders you&apos;re eligible for.
                    </div>
                  </div>
                </div>

                <div className="concierge-feat">
                  <div className="cfeat-icon">⚡</div>
                  <div>
                    <div className="cfeat-title">
                      Priority Processing &amp; Faster Turnarounds
                    </div>
                    <div className="cfeat-desc">
                      Jump the queue — your application gets flagged for faster
                      review and faster disbursal.
                    </div>
                  </div>
                </div>

                <div className="concierge-feat">
                  <div className="cfeat-icon">🔔</div>
                  <div>
                    <div className="cfeat-title">
                      Smart Monitoring &amp; Early Refinance Alerts
                    </div>
                    <div className="cfeat-desc">
                      Get notified when you&apos;re eligible for better terms.
                      We track your loan health and flag refinance opportunities
                      early.
                    </div>
                  </div>
                </div>
              </div>

              <div className="concierge-cta-row">
                <button className="concierge-btn-outline" type="button">
                  Learn more
                </button>
                <button className="concierge-btn-fill" type="button">
                  Get Concierge Access →
                </button>
              </div>
            </div>
          </>
        ),
      },
      {
        cat: "rates",
        q: "Why do all the offers have the same interest rate and processing fee?",
        a: (
          <>
            <p>
              We standardize the interest rate and processing fee across every
              lender so you can compare offers fairly. Instead of competing on
              price, our lenders compete on what actually helps you — how
              quickly they fund, how much they&apos;ll lend, tenor flexibility,
              and service.
            </p>
            <p>
              Every rate also sits within the limits set by the Ministry of Law,
              so you&apos;re always protected.
            </p>
          </>
        ),
      },
      {
        cat: "rates",
        q: "I've seen lower rates advertised elsewhere. Why not on SmartLend?",
        a: (
          <>
            <p>
              Some lenders advertise a very low rate to attract borrowers, then
              adjust it upward once you visit the branch and they review your
              documents. We don&apos;t allow that &quot;bait-and-switch&quot;
              here.
            </p>
            <p>
              On SmartLend, the rate shown is the rate the lender commits to
              honour — no surprise increases after you&apos;ve already chosen
              them. A headline rate advertised elsewhere often isn&apos;t the
              rate you actually get once fees and post-assessment changes are
              added in.
            </p>
          </>
        ),
      },
      {
        cat: "rates",
        q: "Can the rate still change after I pick a lender?",
        a: (
          <>
            <p>
              The rate you see is the committed ceiling — a lender can never
              start higher than it. After you visit the branch and they complete
              their due diligence, there may be room to negotiate the rate down
              further.
            </p>
            <p>In other words, it only ever moves in your favour.</p>
          </>
        ),
      },
      {
        cat: "rates",
        q: "If every lender charges the same, how do I choose one?",
        a: (
          <p>
            Focus on what actually differs between offers: how fast the lender
            can disburse funds, the loan amount they&apos;re offering, tenor
            flexibility, the documents they require, and their reputation and
            service. These are the things that genuinely affect your borrowing
            experience — not a headline number.
          </p>
        ),
      },
      {
        cat: "rates",
        q: "Are these rates within legal limits?",
        a: (
          <p>
            Yes. All interest rates and fees on SmartLend follow the caps set by
            the Ministry of Law — a maximum of 4% monthly interest and a
            processing fee of up to 10% of the principal. You&apos;ll never be
            quoted above the legal limit on our platform.
          </p>
        ),
      },
    ],
    [],
  );

  const [activeCat, setActiveCat] = useState("all");
  const [openKey, setOpenKey] = useState(null);

  const visibleFaqs = useMemo(() => {
    if (activeCat === "all") return FAQS;
    return FAQS.filter((f) => f.cat === activeCat);
  }, [FAQS, activeCat]);

  const onPickCat = (catKey) => {
    setActiveCat(catKey);
    setOpenKey(null); // close any open accordion when switching tabs
  };

  const toggle = (idx) => {
    const key = `${activeCat}:${idx}`;
    setOpenKey((prev) => (prev === key ? null : key)); // single-open behavior
  };

  const acceptedOfferStatuses = useMemo(
    () =>
      new Set([
        "awaiting_lender_documents",
        "pending_borrower_signature",
        "pending_lender_confirmation",
        "revision_requested",
        "pending_disbursement",
        "ongoing",
        "funded",
      ]),
    [],
  );
  const awaitingLenderStatuses = useMemo(
    () =>
      new Set([
        "borrower_renegotiation",
        "awaiting_lender_documents",
        "pending_lender_confirmation",
      ]),
    [],
  );
  const statementCoverageEnabled =
    userDetails?.active_email === "iaaijojojo97@gmail.com" ||
    userDetails?.email === "iaaijojojo97@gmail.com" ||
    userDetails?.active_email === "towkay@smart-towkay.com" ||
    userDetails?.email === "towkay@smart-towkay.com";
  const myinfoPayload = useMemo(
    () => getMyInfoData(userDetails?.myinfo_data),
    [userDetails],
  );
  const loanCapDetails = useMemo(() => {
    const cpfHistory = myinfoPayload?.cpfcontributions?.history || [];
    if (!cpfHistory.length) {
      return { cap: null, source: null, requiresPayslipUpload: true };
    }

    const monthlyTotals = cpfHistory.reduce((acc, entry) => {
      const month = entry?.month?.value;
      if (!month) return acc;
      const amount = Number(entry?.amount?.value || 0);
      acc[month] = (acc[month] || 0) + amount;
      return acc;
    }, {});

    const now = new Date();
    const getWindow = (startOffset) =>
      [1, 2, 3].map((offset) =>
        format(subMonths(now, offset + startOffset), "yyyy-MM"),
      );
    const windows = [getWindow(0), getWindow(1)];
    const validWindow = windows.find((months) =>
      months.every((month) => month in monthlyTotals),
    );

    if (!validWindow) {
      return { cap: null, source: null, requiresPayslipUpload: true };
    }

    const totalCpf = validWindow.reduce(
      (sum, month) => sum + (monthlyTotals[month] || 0),
      0,
    );
    const annualIncome = totalCpf * 10.81;
    const cap = annualIncome / 3;
    const label = [...validWindow]
      .reverse()
      .map((month) => format(new Date(`${month}-01`), "MMM yyyy"))
      .join(" - ");

    return {
      cap,
      source: `CPF (${label})`,
      requiresPayslipUpload: false,
    };
  }, [myinfoPayload]);
  const requiresPayslipUpload = loanCapDetails?.requiresPayslipUpload;
  const loanCap = loanCapDetails?.cap;
  const applyMaxAmount = useMemo(() => {
    if (!loanCap || Number.isNaN(Number(loanCap))) {
      return 100000;
    }
    return Math.max(Math.floor(Number(loanCap)), 1);
  }, [loanCap]);
  const applySteps = useMemo(() => {
    const steps = [
      { key: "contact", title: "Contact" },
      ...(requiresPayslipUpload
        ? [{ key: "payslips", title: "Payslips" }]
        : []),
      { key: "cbs", title: "CBS report" },
      { key: "authorization", title: "Authorization" },
      { key: "visibility", title: "Visibility" },
    ];
    return steps;
  }, [requiresPayslipUpload]);
  const currentApplyStep = applySteps[applyStep]?.key || "contact";

  const fetchEligibility = async () => {
    try {
      setEligibilityLoading(true);
      const response = await fetch(
        "https://www.smart-lend.com/api/personalLoans/eligibility",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) throw new Error("Unable to fetch eligibility");
      const json = await response.json();
      setEligibility(json);
    } catch (error) {
      console.error(error);
      message.error("Unable to check eligibility.");
    } finally {
      setEligibilityLoading(false);
    }
  };

  const fetchMyLoans = async () => {
    try {
      setLoadingLoans(true);
      const response = await fetch(
        "https://www.smart-lend.com/api/personalLoans/my",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) throw new Error("Unable to load Money Lender loans");
      const json = await response.json();
      setMyLoans(json.data || []);
    } catch (error) {
      console.error(error);
      message.error("Unable to load Money Lender loans.");
    } finally {
      setLoadingLoans(false);
    }
  };

  const fetchOfferAlert = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/loan/offer-alert?product=personal",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) throw new Error("Unable to check offer alert");
      const json = await response.json();
      if (activeTab !== "requested" && shouldShowOfferAlert(json)) {
        setOfferAlert({ open: true, data: json });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const closeOfferAlert = () => {
    dismissOfferAlert(offerAlert.data);
    setOfferAlert({ open: false, data: null });
  };

  const viewOfferFromAlert = () => {
    dismissOfferAlert(offerAlert.data);
    setOfferAlert({ open: false, data: null });
    setActiveTab("requested");
  };

  const fetchUserDetails = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/profile/getUserDetails",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.ok) {
        const json = await response.json();
        setUserDetails(json.User);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchStatementCoverage = async (loanId) => {
    try {
      setStatementCoverageLoading((prev) => ({ ...prev, [loanId]: true }));
      const response = await fetch(
        `https://www.smart-lend.com/api/bank-document-jobs/coverage?loan_id=${loanId}&loan_type=personal`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to load statement coverage.");
      }
      setStatementCoverageMap((prev) => ({ ...prev, [loanId]: json }));
    } catch (error) {
      console.error(error);
    } finally {
      setStatementCoverageLoading((prev) => ({ ...prev, [loanId]: false }));
    }
  };

  const openStatementCoverageModal = (loan, missingMonths) => {
    setStatementCoverageUploads({});
    setStatementCoverageModal({
      open: true,
      loan,
      missingMonths: missingMonths || [],
    });
  };

  const closeStatementCoverageModal = () => {
    setStatementCoverageModal({ open: false, loan: null, missingMonths: [] });
    setStatementCoverageUploads({});
  };

  const handleStatementCoverageUpload = (monthKey, files) => {
    setStatementCoverageUploads((prev) => ({
      ...prev,
      [monthKey]: Array.from(files || []),
    }));
  };

  const submitStatementCoverageUploads = async () => {
    if (!statementCoverageModal.loan) return;
    const fileId = `personal_loan_request:${statementCoverageModal.loan.id}`;
    try {
      setStatementCoverageSubmitting(true);
      const monthDocuments = await Promise.all(
        statementCoverageModal.missingMonths.map(async (month) => {
          const files = statementCoverageUploads[month.key] || [];
          if (!files.length) return null;
          const uploaded = await Promise.all(
            files.map((file) => uploadPersonalLoanFile(file, fileId)),
          );
          return {
            month_key: month.key,
            month_label: month.label,
            documents: uploaded,
          };
        }),
      );

      const payload = monthDocuments.filter(Boolean);
      if (!payload.length) {
        message.warning(
          "Please upload bank statements for at least one missing month.",
        );
        return;
      }

      const response = await fetch(
        `https://www.smart-lend.com/api/personalLoans/${statementCoverageModal.loan.id}/bank-statements`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ bank_statement_documents: payload }),
        },
      );

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to upload bank statements.");
      }

      message.success("Bank statements added. We'll review them shortly.");
      closeStatementCoverageModal();
      fetchStatementCoverage(statementCoverageModal.loan.id);
    } catch (error) {
      console.error(error);
      message.error(
        error?.message || "Unable to upload missing bank statements.",
      );
    } finally {
      setStatementCoverageSubmitting(false);
    }
  };

  const fetchCbsPromoCode = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/campaign/getcbscode",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Unable to fetch CBS promo code");
      }
      const json = await response.json();
      setCbsPromoCode(json.cbs_campaign_code || "");
    } catch (error) {
      console.error(error);
      message.error("Unable to retrieve CBS promo code.");
    }
  };

  useEffect(() => {
    fetchEligibility();
    fetchMyLoans();
    fetchUserDetails();
    fetchOfferAlert();
  }, []);

  useEffect(() => {
    if (userDetails) {
      setContactEmail(
        (prev) => prev || userDetails.active_email || userDetails.email || "",
      );
      setContactPhone(
        (prev) =>
          prev || userDetails.active_mobile_no || userDetails.mobile_no || "",
      );
    }
  }, [userDetails]);

  useEffect(() => {
    if (currentApplyStep === "cbs" && applyModalOpen) {
      fetchCbsPromoCode();
    }
  }, [currentApplyStep, applyModalOpen]);

  useEffect(() => {
    if (!statementCoverageEnabled || !myLoans.length) return;
    myLoans.forEach((loan) => {
      if (!statementCoverageMap[loan.id]) {
        fetchStatementCoverage(loan.id);
      }
    });
  }, [statementCoverageEnabled, myLoans]);

  useEffect(() => {
    async function fetchAds() {
      const response = await fetch(
        "https://www.smart-lend.com/api/ads/getAdsByScreen?screen=bApplyLoan",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch ads");
      }

      const data = await response.json();
      setDisplayAds(data.ads || []);
      if (data.ads?.length) {
        setAdImage(data.ads[0].image);
        setAdLink(data.ads[0].link);
      }
    }

    fetchAds().catch((error) => {
      console.error(error);
    });
  }, []);

  useEffect(() => {
    if (displayAds.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % displayAds.length);
      }, 3200);

      return () => clearInterval(interval);
    }

    return undefined;
  }, [displayAds]);

  useEffect(() => {
    if (displayAds.length > 0) {
      setAdImage(displayAds[currentIndex].image);
      setAdLink(displayAds[currentIndex].link);
    }
  }, [currentIndex, displayAds]);

  const allOffers = useMemo(
    () =>
      myLoans.flatMap((loan) =>
        (loan.offers || []).map((offer) => ({ ...offer, loan })),
      ),
    [myLoans],
  );
  const mlcbPendingLoans = useMemo(() => {
    const matchesMlcbText = (value) =>
      String(value || "")
        .toLowerCase()
        .includes("mlcb");
    const isWithinWindow = (loan) => {
      if (!loan?.created_at) return true;
      const createdAt = new Date(loan.created_at);
      if (Number.isNaN(createdAt.getTime())) return true;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      return createdAt >= cutoff;
    };
    const hasMlcbUpload = (loan) =>
      !!loan?.mlcb_report_document ||
      (loan.offers || []).some((offer) =>
        (offer.documents || []).some(
          (doc) =>
            doc.role === "borrower" &&
            matchesMlcbText(
              doc?.metadata?.requested_document || doc?.notes || doc?.file_name,
            ),
        ),
      );

    return myLoans.filter(
      (loan) =>
        loan.allow_lender_visibility === true &&
        isWithinWindow(loan) &&
        !hasMlcbUpload(loan),
    );
  }, [myLoans]);
  const mlcbRequestedOffers = useMemo(
    () =>
      mlcbPendingLoans.flatMap((loan) =>
        (loan.offers || []).map((offer) => ({ offer, loan })),
      ),
    [mlcbPendingLoans],
  );
  useEffect(() => {
    if (
      activeTab === "requested" &&
      mlcbPendingLoans.length > 0 &&
      !mlcbModalOpen
    ) {
      setMlcbModalOpen(true);
    }
  }, [activeTab, mlcbPendingLoans]);
  const requestedOffers = useMemo(
    () =>
      allOffers.filter(
        (offer) =>
          !ongoingStatuses.has(offer.status) &&
          !awaitingLenderStatuses.has(offer.status),
      ),
    [allOffers, awaitingLenderStatuses, ongoingStatuses],
  );
  const awaitingLenderOffers = useMemo(
    () => allOffers.filter((offer) => awaitingLenderStatuses.has(offer.status)),
    [allOffers, awaitingLenderStatuses],
  );
  const ongoingOffers = useMemo(
    () => allOffers.filter((offer) => ongoingStatuses.has(offer.status)),
    [allOffers, ongoingStatuses],
  );
  const acceptOfferAdjustment = useMemo(() => {
    if (!offerAction.offer || offerAction.action !== "accept") return null;
    const loan = offerAction.offer.loan;
    if (!loan) return null;
    const offerAmount = Number(offerAction.offer.offer_data?.amount || 0);
    const acceptedAmount = (loan.offers || [])
      .filter(
        (offer) =>
          offer.id !== offerAction.offer.id &&
          acceptedOfferStatuses.has(offer.status),
      )
      .reduce(
        (total, offer) => total + Number(offer.offer_data?.amount || 0),
        0,
      );
    const remainingAmount = Math.max(
      Number(loan.amount || 0) - acceptedAmount,
      0,
    );
    const adjustedAmount =
      offerAmount > 0 ? Math.min(offerAmount, remainingAmount) : 0;

    if (adjustedAmount <= 0) {
      return {
        remainingAmount,
        adjustedAmount: 0,
        offerAmount,
        isAdjusted: false,
      };
    }

    return {
      remainingAmount,
      adjustedAmount,
      offerAmount,
      isAdjusted: adjustedAmount < offerAmount,
      acceptedAmount,
    };
  }, [acceptedOfferStatuses, offerAction.action, offerAction.offer]);
  const acceptOfferMaximum = useMemo(() => {
    if (!acceptOfferAdjustment) return 0;
    return Number(acceptOfferAdjustment.adjustedAmount || 0);
  }, [acceptOfferAdjustment]);

  useEffect(() => {
    if (offerAction.action === "accept" && offerAction.offer) {
      setAcceptOfferAmount(acceptOfferMaximum || null);
    } else {
      setAcceptOfferAmount(null);
    }
  }, [acceptOfferMaximum, offerAction.action, offerAction.offer]);

  const acceptOfferMonthlyInstallment = useMemo(() => {
    if (!offerAction.offer || offerAction.action !== "accept") return null;
    const offerData = offerAction.offer.offer_data || {};
    const ratePm = Number((offerData.rate_pm ?? offerData.rate_pa) || 0);
    const tenorMonths = Number(offerData.tenor_years || 0) * 12;
    return calculateMonthlyInstallment(acceptOfferAmount, ratePm, tenorMonths);
  }, [acceptOfferAmount, offerAction.action, offerAction.offer]);

  const applyMonthlyRate = useMemo(
    () => Number(applyForm.desired_rate_pa || 0),
    [applyForm.desired_rate_pa],
  );

  const applyEstimatedMonthlyInstallment = useMemo(() => {
    return calculateMonthlyInstallment(
      applyForm.amount,
      applyMonthlyRate,
      applyForm.desired_tenor_months,
    );
  }, [applyForm.amount, applyForm.desired_tenor_months, applyMonthlyRate]);

  const isWithinMasMonthlyInterestLimit =
    applyMonthlyRate >= MIN_PREFERRED_INTEREST_RATE &&
    applyMonthlyRate <= MAX_PREFERRED_INTEREST_RATE;
  const isAllowedTenorMonth = ALLOWED_TENOR_MONTH_OPTIONS.includes(
    Number(applyForm.desired_tenor_months),
  );

  const hasMyInfoData = !!myinfoPayload;
  const canApply = Boolean(eligibility?.can_apply) && hasMyInfoData;
  const eligibilityMessage = hasMyInfoData
    ? eligibility?.message
    : "Please complete your MyInfo profile before applying for a personal loan.";
  const canProceedContact = !!contactEmail && !!contactPhone;
  const canProceedPayslips = !requiresPayslipUpload || payslipFiles.length > 0;
  const isCbsWithinValidPeriod = isCbsEnquiryDateValid(cbsEnquiryDate);
  const canProceedCbs = cbsFiles.length > 0 && isCbsWithinValidPeriod;
  const canSubmitApply =
    canProceedContact &&
    canProceedPayslips &&
    canProceedCbs &&
    authorizationChecked &&
    !applyBusy &&
    (!loanCap || !loanAmountExceeded) &&
    isWithinMasMonthlyInterestLimit &&
    isAllowedTenorMonth;
  const nextDisabled =
    (currentApplyStep === "contact" && !canProceedContact) ||
    (currentApplyStep === "payslips" && !canProceedPayslips) ||
    (currentApplyStep === "cbs" && !canProceedCbs) ||
    (currentApplyStep === "authorization" && !authorizationChecked);
  const cbsFileList = cbsFiles.map((file) => ({
    uid: file.uid || `${file.name}-${file.lastModified}`,
    name: file.name,
    status: "done",
  }));
  const payslipFileList = payslipFiles.map((file) => ({
    uid: file.uid || `${file.name}-${file.lastModified}`,
    name: file.name,
    status: "done",
  }));
  const mlcbFileList = mlcbFiles.map((file) => ({
    uid: file.uid || `${file.name}-${file.lastModified}`,
    name: file.name,
    status: "done",
  }));

  const resetApplyModal = () => {
    setApplyStep(0);
    setPayslipFiles([]);
    setCbsFiles([]);
    setCbsFileText("");
    setCbsExtractedData(null);
    setCbsEnquiryDate(null);
    setAuthorizationChecked(false);
    setAllowLenderVisibility(true);
    setLoanAmountExceeded(false);
  };

  const openApplyModal = () => {
    if (!canApply) return;
    resetApplyModal();
    setApplyModalOpen(true);
  };

  const closeApplyModal = () => {
    setApplyModalOpen(false);
    resetApplyModal();
  };

  const uploadPersonalLoanFile = async (file, fileId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "personal_loan_request");
    formData.append("file_id", fileId);

    const response = await fetch(
      "https://www.smart-lend.com/api/file/uploadUniversalFile",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: formData,
      },
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data?.message || "File upload failed.");
    }

    const data = await response.json();
    return {
      document_id: data.document_id,
      file_name: data.original_filename,
      file_url: data.url,
    };
  };

  const handleApplySubmit = async () => {
    if (!canApply) return;

    try {
      setApplyBusy(true);
      const fileId = `personal_loan_request:${userDetails?.user_id || "draft"}`;
      const payslipUploads = await Promise.all(
        payslipFiles.map((file) => uploadPersonalLoanFile(file, fileId)),
      );

      const cbsDocumentUploads = await Promise.all(
        cbsFiles.map((file) => uploadPersonalLoanFile(file, fileId)),
      );
      const cbsDocument = cbsDocumentUploads[0];
      if (!cbsDocument) {
        throw new Error("CBS report upload is required.");
      }
      const response = await fetch(
        "https://www.smart-lend.com/api/personalLoans/apply",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            ...applyForm,
            desired_rate_pa: normalizePreferredInterestRate(
              applyForm.desired_rate_pa,
            ),
            desired_tenor_months: normalizePreferredTenorMonths(
              applyForm.desired_tenor_months,
            ),
            contact_email: contactEmail,
            contact_phone: contactPhone,
            payslip_documents: payslipUploads,
            cbs_report_document: {
              ...cbsDocument,
              promo_code: cbsPromoCode || null,
              extracted_data: cbsExtractedData || null,
            },
            allow_lender_visibility: allowLenderVisibility,
            meta: {
              application_source: hasMyInfoData ? "myinfo" : null,
            },
          }),
        },
      );

      const json = await response.json();
      if (!response.ok) {
        throw new Error(
          json?.message || "Unable to submit Money Lender loan request",
        );
      }

      message.success(
        "Loan request submitted. We'll notify lenders immediately.",
      );
      closeApplyModal();
      fetchEligibility();
      await fetchMyLoans();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to submit loan request.");
    } finally {
      setApplyBusy(false);
    }
  };

  const openOfferModal = (action, offer) => {
    setOfferAction({ open: true, action, offer });
    setOfferMessage("");
  };

  const resetMlcbModal = () => {
    setMlcbFiles([]);
    setMlcbUploadBusy(false);
  };

  const closeMlcbModal = () => {
    setMlcbModalOpen(false);
    resetMlcbModal();
  };

  const uploadMlcbReport = async (loanId, file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(
      `https://www.smart-lend.com/api/personalLoans/requests/${loanId}/mlcb-report`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: formData,
      },
    );

    const data = await response.json();
    if (!response.ok || !data?.document?.document_id) {
      throw new Error(data?.message || "Unable to upload MLCB report");
    }

    return data;
  };

  const handleMlcbUploadSubmit = async () => {
    if (!mlcbFiles.length) {
      message.warning("Please upload your MLCB report.");
      return;
    }

    try {
      setMlcbUploadBusy(true);
      const [file] = mlcbFiles;
      const loansToUpload = mlcbPendingLoans
        .map((loan) => loan?.id)
        .filter(Boolean);
      if (loansToUpload.length === 0) {
        message.warning("No pending MLCB requests found.");
        closeMlcbModal();
        return;
      }

      const loansWithOffers = mlcbRequestedOffers.reduce((acc, entry) => {
        const loanId = entry.loan?.id;
        if (!loanId) {
          return acc;
        }
        if (!acc.has(loanId)) {
          acc.set(loanId, { loan: entry.loan, offers: [] });
        }
        acc.get(loanId).offers.push(entry.offer);
        return acc;
      }, new Map());

      const uploadResults = new Map();
      for (const loanId of loansToUpload) {
        const uploadData = await uploadMlcbReport(loanId, file);
        uploadResults.set(loanId, uploadData);
      }

      for (const entry of loansWithOffers.values()) {
        const uploadData = uploadResults.get(entry.loan.id);
        if (!uploadData) {
          continue;
        }
        for (const offer of entry.offers) {
          const attachResponse = await fetch(
            `https://www.smart-lend.com/api/personalLoans/offers/${offer.id}/documents`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Cookies.get("token")}`,
              },
              body: JSON.stringify({
                document_id: uploadData.document.document_id,
                stage: "supporting",
                notes: "MLCB report",
                metadata: { requested_document: "MLCB report" },
              }),
            },
          );

          const attachJson = await attachResponse.json();
          if (!attachResponse.ok) {
            throw new Error(attachJson?.message || "Unable to attach document");
          }
        }
      }

      message.success("MLCB report uploaded. We'll notify the lenders.");
      closeMlcbModal();
      await fetchMyLoans();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to upload MLCB report.");
    } finally {
      setMlcbUploadBusy(false);
    }
  };

  const handleCbsBeforeUpload = async (file) => {
    setCbsFiles([file]);
    try {
      const text = await pdfToText(file);
      setCbsFileText(text);
      setCbsExtractedData(parseCbsData(text));
      setCbsEnquiryDate(extractCbsEnquiryDate(text));
    } catch (error) {
      console.error("Failed to extract text from CBS report");
      setCbsFileText("");
      setCbsExtractedData(null);
      setCbsEnquiryDate(null);
    }
    return false;
  };

  const handleNextApplyStep = () => {
    if (currentApplyStep === "contact") {
      if (!contactEmail || !contactPhone) {
        message.warning("Please provide both email and phone number.");
        return;
      }
    }
    if (currentApplyStep === "payslips" && requiresPayslipUpload) {
      if (!payslipFiles.length) {
        message.warning("Please upload your last 3 months of payslips.");
        return;
      }
    }
    if (currentApplyStep === "cbs") {
      if (!cbsFiles.length) {
        message.warning("Please upload your CBS report.");
        return;
      }
      if (!isCbsWithinValidPeriod) {
        message.warning(
          "The CBS report enquiry date must be within the last month.",
        );
        return;
      }
    }
    if (currentApplyStep === "authorization" && !authorizationChecked) {
      message.warning("Please confirm the authorization to continue.");
      return;
    }
    setApplyStep((prev) => Math.min(prev + 1, applySteps.length - 1));
  };

  const handlePreviousApplyStep = () => {
    setApplyStep((prev) => Math.max(prev - 1, 0));
  };

  const closeOfferModal = () => {
    setOfferAction({ open: false, action: null, offer: null });
    setOfferMessage("");
    setOfferSubmitting(false);
    setAcceptOfferAmount(null);
  };

  const submitOfferAction = async () => {
    const { action, offer } = offerAction;
    if (!offer || !action) return;

    const trimmed = offerMessage.trim();
    if (action === "renegotiate" && !trimmed) {
      message.warning("Please add a short note for the lender.");
      return;
    }

    if (action === "accept") {
      if (!acceptOfferMaximum || acceptOfferMaximum <= 0) {
        message.warning("This loan request is already fully funded.");
        return;
      }
      if (!acceptOfferAmount || acceptOfferAmount <= 0) {
        message.warning("Please enter an amount to accept.");
        return;
      }
      if (acceptOfferAmount > acceptOfferMaximum) {
        message.warning("Accepted amount cannot exceed the available amount.");
        return;
      }
    }

    try {
      setOfferSubmitting(true);
      const response = await fetch(
        `https://www.smart-lend.com/api/personalLoans/offers/${offer.id}/borrower-response`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            action,
            message: trimmed || null,
            accepted_amount:
              action === "accept" && acceptOfferAmount
                ? Number(acceptOfferAmount)
                : null,
          }),
        },
      );

      const json = await response.json();
      if (!response.ok)
        throw new Error(json?.message || "Unable to update offer");

      const success =
        action === "accept"
          ? "Offer accepted"
          : action === "reject"
            ? "Offer rejected"
            : "Renegotiation request sent";

      message.success(success);
      closeOfferModal();
      await fetchMyLoans();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to update offer.");
    } finally {
      setOfferSubmitting(false);
    }
  };

  const handleUploadNotesChange = (offerId, value) => {
    setUploadNotesMap((prev) => ({ ...prev, [offerId]: value }));
  };

  const triggerFileDialog = (offerId) => {
    const input = fileInputRefs.current[offerId];
    if (input) input.click();
  };

  const handleDocumentUpload = async (offer, files) => {
    if (!files.length) return;

    const notes = (uploadNotesMap[offer.id] || "").trim();
    let successCount = 0;

    try {
      setUploadingOfferId(offer.id);

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "personal_loan");
        formData.append("file_id", offer.id);

        const uploadResponse = await fetch(
          "https://www.smart-lend.com/api/file/uploadUniversalFile",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${Cookies.get("token")}`,
            },
            body: formData,
          },
        );

        const uploadJson = await uploadResponse.json();
        if (uploadResponse.status !== 200 || !uploadJson?.document_id) {
          throw new Error(uploadJson?.message || "Unable to upload document");
        }

        const attachResponse = await fetch(
          `https://www.smart-lend.com/api/personalLoans/offers/${offer.id}/documents`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${Cookies.get("token")}`,
            },
            body: JSON.stringify({
              document_id: uploadJson.document_id,
              stage: "signed",
              notes: notes || null,
            }),
          },
        );

        const attachJson = await attachResponse.json();
        if (!attachResponse.ok) {
          throw new Error(attachJson?.message || "Unable to register document");
        }

        successCount += 1;
      }

      if (successCount > 0) {
        message.success(
          successCount === 1
            ? "Document uploaded"
            : `${successCount} documents uploaded`,
        );
        setUploadNotesMap((prev) => ({ ...prev, [offer.id]: "" }));
        await fetchMyLoans();
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to upload document");
    } finally {
      setUploadingOfferId(null);
    }
  };

  const onFileChange = (offer, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) {
      handleDocumentUpload(offer, files);
    }
    event.target.value = "";
  };

  const triggerSupportingDialog = (offerId, docIndex) => {
    const input = supportingFileRefs.current?.[offerId]?.[docIndex];
    if (input) input.click();
  };

  const handleSupportingDocumentUpload = async (offer, docLabel, files) => {
    if (!files.length) return;
    const busyKey = `${offer.id}-${docLabel}`;
    try {
      setSupportingUploadBusy((prev) => ({ ...prev, [busyKey]: true }));

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "personal_loan");
        formData.append("file_id", offer.id);

        const uploadResponse = await fetch(
          "https://www.smart-lend.com/api/file/uploadUniversalFile",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${Cookies.get("token")}`,
            },
            body: formData,
          },
        );

        const uploadJson = await uploadResponse.json();
        if (uploadResponse.status !== 200 || !uploadJson?.document_id) {
          throw new Error(uploadJson?.message || "Unable to upload document");
        }

        const attachResponse = await fetch(
          `https://www.smart-lend.com/api/personalLoans/offers/${offer.id}/documents`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${Cookies.get("token")}`,
            },
            body: JSON.stringify({
              document_id: uploadJson.document_id,
              stage: "supporting",
              notes: docLabel,
              metadata: { requested_document: docLabel },
            }),
          },
        );

        const attachJson = await attachResponse.json();
        if (!attachResponse.ok) {
          throw new Error(attachJson?.message || "Unable to attach document");
        }
      }

      message.success("Document uploaded.");
      await fetchMyLoans();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to upload document");
    } finally {
      setSupportingUploadBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handleConfirmDocuments = async (offer) => {
    const messageValue = (uploadNotesMap[offer.id] || "").trim();
    try {
      setConfirmingOfferId(offer.id);
      const response = await fetch(
        `https://www.smart-lend.com/api/personalLoans/offers/${offer.id}/confirm-signed`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({ message: messageValue || null }),
        },
      );

      const json = await response.json();
      if (!response.ok) throw new Error(json?.message || "Unable to confirm");

      message.success("Thanks! We've notified the lender.");
      setUploadNotesMap((prev) => ({ ...prev, [offer.id]: "" }));
      await fetchMyLoans();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to confirm documents");
    } finally {
      setConfirmingOfferId(null);
    }
  };

  const openRepaymentScheduleModal = (offer) => {
    setRepaymentScheduleModal({ open: true, offer });
  };

  const closeRepaymentScheduleModal = () => {
    setRepaymentScheduleModal({ open: false, offer: null });
  };

  const handleGetLenderProfile = async (selectedLenderId) => {
    if (!selectedLenderId) return;
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/user/getLenderProfileDesign",
        {
          method: "POST",
          body: JSON.stringify({ user_id: selectedLenderId }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        },
      );

      const json = await response.json();
      if (json.design !== null) {
        setLenderProfileData(JSON.parse(json.design));
        console.log(JSON.parse(json.design));
      } else {
        setLenderProfileData(null);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const handleMlcbBeforeUpload = async (file) => {
    setMlcbFiles([file]);
    return false; // stop antd auto upload
  };

  const renderHistory = (offer) => {
    const history = Array.isArray(offer.status_history)
      ? [...offer.status_history].reverse()
      : [];

    if (!history.length) return null;

    const items = history.map((entry, index) => ({
      color: HISTORY_COLORS[entry.actor] || "gray",
      children: (
        <div key={`history-${offer.id}-${index}`}>
          <div style={{ fontWeight: 600, fontSize: "13px" }}>
            {HISTORY_COPY[entry.action] || entry.action}
          </div>
          {entry.message && (
            <div style={{ marginTop: "4px", fontSize: "13px" }}>
              {entry.message}
            </div>
          )}
          {entry.disbursement_date && (
            <div style={{ marginTop: "4px", fontSize: "11px" }}>
              Disbursement date: {formatDate(entry.disbursement_date)}
            </div>
          )}
          <div
            style={{
              marginTop: "4px",
              fontSize: "11px",
              color: "rgba(10, 61, 98, 0.6)",
            }}
          >
            {(entry.actor === "borrower" && "You") ||
              (entry.actor === "lender" && "Lender") ||
              "System"}
            {" · "}
            {formatDateTime(entry.timestamp)}
          </div>
        </div>
      ),
    }));

    return (
      <div style={{ marginTop: "16px" }}>
        <div
          style={{ fontWeight: 600, fontSize: "13px", color: PRIMARY_COLOR }}
        >
          Activity
        </div>
        <Timeline style={{ marginTop: "8px" }} items={items} />
      </div>
    );
  };

  const renderOfferCard = (offer, options = {}) => {
    const { showLenderProfile = false } = options;
    const statusDisplay = getStatusDisplay(offer.status);
    const offerData = offer.offer_data || {};
    const offerQuantum = Number(offerData.amount || 0);
    const processingFeeAmount = offerData.processing_fee != null
      ? Number(offerData.processing_fee)
      : offerQuantum * PROCESSING_FEE_RATE;
    const processingFeeLabel = offerData.processing_fee_type === "fixed"
      ? formatCurrency(processingFeeAmount)
      : `${offerData.processing_fee_type === "percentage"
        ? Number(offerData.processing_fee_value)
        : offerQuantum > 0 ? Number(((processingFeeAmount / offerQuantum) * 100).toFixed(2)) : 0}% (${formatCurrency(processingFeeAmount)})`;
    const disbursementAmount = Math.max(offerQuantum - processingFeeAmount, 0);
    const loanExpired = false;
    const requestedDocuments = (offerData.requested_documents || [])
      .map((doc) => (typeof doc === "string" ? doc : doc?.name))
      .filter(Boolean);
    const documentsFromLender = (offer.documents || []).filter(
      (doc) => doc.role === "lender",
    );
    const documentsFromBorrower = (offer.documents || []).filter(
      (doc) => doc.role === "borrower",
    );
    const supportingDocs = documentsFromBorrower.filter(
      (doc) => doc.stage === "supporting",
    );
    const supportingDocsByRequest = supportingDocs.reduce((acc, doc) => {
      const key =
        doc?.metadata?.requested_document || doc?.notes || "Other documents";
      if (!acc[key]) acc[key] = [];
      acc[key].push(doc);
      return acc;
    }, {});
    const repaymentSchedules = (offer.repayment_schedules || [])
      .slice()
      .sort(
        (a, b) =>
          new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
      );
    const pendingSchedules = repaymentSchedules.filter(
      (schedule) => !isRepaymentSettled(schedule),
    );
    const remainingTenor = pendingSchedules.length;
    const outstandingBalance =
      pendingSchedules.length > 0
        ? pendingSchedules.reduce(
            (total, schedule) => total + Number(schedule.amount_due || 0),
            0,
          )
        : 0;
    const noteValue = uploadNotesMap[offer.id] || "";
    const lenderName =
      offer.lender?.entity_name || offer.lender?.principal_name || "Lender";
    const lenderId =
      offer.lender?.user_id || offer.lender_id || offer.lender?.user_id;
    let lenderProfilePhoto =
      offer.lender?.profile_photo || offer.lender?.profile?.profile_photo;

    if (!lenderProfilePhoto && offer.lender?.profile_design) {
      try {
        const profileDesign =
          typeof offer.lender.profile_design === "string"
            ? JSON.parse(offer.lender.profile_design)
            : offer.lender.profile_design;
        lenderProfilePhoto = profileDesign?.profile?.profile_photo;
      } catch (error) {
        console.error("Unable to parse lender profile design", error);
      }
    }
    const canAct = [
      "pending_borrower_action",
      "borrower_renegotiation",
    ].includes(offer.status);
    const canUpload = [
      "pending_borrower_signature",
      "revision_requested",
    ].includes(offer.status);
    const isOngoing = ongoingStatuses.has(offer.status);
    const actionDisabled = loanExpired;

    return (
      <div
        key={offer.id}
        className={["slCard", isMobile ? "isMobile" : ""].join(" ")}
        style={{ marginTop: 20, color: PRIMARY_COLOR }}
      >
        <div>
          <div className="slTopRow">
            <div className="slPills">
              <span
                className={[
                  "slPill",
                  statusDisplay.color === "red"
                    ? "danger"
                    : statusDisplay.color === "green"
                      ? "success"
                      : statusDisplay.color === "gold" ||
                          statusDisplay.color === "orange"
                        ? "warning"
                        : "info",
                ].join(" ")}
              >
                {statusDisplay.label}
              </span>
              {loanExpired ? (
                <span className="slPill danger">Expired</span>
              ) : null}
              <span className="slPill">Offer #{offer.id}</span>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Tooltip
                title={
                  lenderId
                    ? "View lender profile"
                    : "Lender profile unavailable"
                }
              >
                <div
                  role={lenderId ? "button" : undefined}
                  tabIndex={lenderId ? 0 : undefined}
                  style={{
                    width: "58px",
                    height: "58px",
                    borderRadius: "50%",
                    background: "rgb(140,140,170)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "white",
                    fontSize: 20,
                    fontWeight: 600,
                    overflow: "hidden",
                    cursor: lenderId ? "pointer" : "default",
                    flexShrink: 0,
                  }}
                  onClick={() => lenderId && handleGetLenderProfile(lenderId)}
                  onKeyDown={(event) => {
                    if (!lenderId) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleGetLenderProfile(lenderId);
                    }
                  }}
                >
                  {lenderProfilePhoto ? (
                    <img
                      src={lenderProfilePhoto}
                      alt={lenderName}
                      style={{
                        width: "58px",
                        height: "58px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    getInitials(lenderName)
                  )}
                </div>
              </Tooltip>
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#1f2a44",
                    lineHeight: 1.15,
                  }}
                >
                  {lenderName}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "rgba(10,61,98,0.6)",
                    marginTop: "4px",
                    fontWeight: 600,
                  }}
                >
                  Updated {formatDateTime(offer.updated_at)}
                </div>
              </div>
            </div>
          </div>

          <div className="slMetaGrid" style={{ marginTop: 12 }}>
            <div className="slMetaItem">
              <div className="slMetaK">Offer quantum</div>
              <div className="slMetaV">{formatCurrency(offerQuantum)}</div>
            </div>

            <div className="slMetaItem">
              <div className="slMetaK">Interest rate</div>
              <div className="slMetaV">
                {formatPercentPerMonth(offerData.rate_pm ?? offerData.rate_pa)}
                {offerData.interest_rate_type && (
                  <div style={{ fontSize: "12px", fontWeight: 400 }}>
                    {offerData.interest_rate_type}
                  </div>
                )}
              </div>
            </div>

            <div className="slMetaItem">
              <div className="slMetaK">Tenor</div>
              <div className="slMetaV">
                {formatMonths(offerData.tenor_years * 12)}
              </div>
            </div>

            <div className="slMetaItem">
              <div className="slMetaK">Processing fee</div>
              <div className="slMetaV">
                {processingFeeLabel}
              </div>
            </div>

            <div className="slMetaItem">
              <div className="slMetaK">Disbursement amount</div>
              <div className="slMetaV">
                {formatCurrency(disbursementAmount)}
              </div>
            </div>

            {isOngoing ? (
              <div className="slMetaItem">
                <div className="slMetaK">Balance</div>
                <div className="slMetaV">
                  {formatCurrency(outstandingBalance)}
                </div>
              </div>
            ) : null}

            {isOngoing ? (
              <div className="slMetaItem" style={{ gridColumn: "1 / -1" }}>
                <div className="slMetaK">Remaining tenor</div>
                <div className="slMetaV">
                  {remainingTenor} month{remainingTenor === 1 ? "" : "s"}
                </div>
              </div>
            ) : null}
          </div>

          {loanExpired ? (
            <div
              style={{
                marginTop: "12px",
                padding: "10px 12px",
                borderRadius: "var(--br-card-radius, 10px)",
                background: "rgba(153, 163, 184, 0.18)",
                color: "rgba(10, 61, 98, 0.65)",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              This loan request has expired. You can no longer accept,
              renegotiate, or reject the offer.
            </div>
          ) : null}

          {offer.lender_notes && (
            <div
              style={{
                marginTop: "12px",
                background: "rgba(10,61,98,0.07)",
                padding: "12px",
                borderRadius: "var(--br-card-radius, 8px)",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: 600 }}>
                Lender note
              </div>
              <div style={{ fontSize: "13px", marginTop: "4px" }}>
                {offer.lender_notes}
              </div>
            </div>
          )}

          {requestedDocuments.length > 0 && (
            <div style={{ marginTop: "12px" }}>
              <div style={{ fontSize: "12px", fontWeight: 600 }}>
                Requested documents
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  marginTop: "6px",
                }}
              >
                {requestedDocuments.map((docName, index) => {
                  const uploads = supportingDocsByRequest[docName] || [];
                  const busyKey = `${offer.id}-${docName}`;
                  return (
                    <div
                      key={`${offer.id}-requested-${index}`}
                      style={{
                        border: "1px solid rgba(10,61,98,0.1)",
                        borderRadius: "8px",
                        padding: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div style={{ fontSize: "13px", fontWeight: 600 }}>
                          {docName}
                        </div>
                        <div>
                          <input
                            type="file"
                            ref={(el) => {
                              if (!supportingFileRefs.current[offer.id]) {
                                supportingFileRefs.current[offer.id] = {};
                              }
                              supportingFileRefs.current[offer.id][index] = el;
                            }}
                            multiple
                            style={{ display: "none" }}
                            onChange={(event) => {
                              const files = Array.from(
                                event.target.files || [],
                              );
                              if (files.length) {
                                handleSupportingDocumentUpload(
                                  offer,
                                  docName,
                                  files,
                                );
                              }
                              event.target.value = "";
                            }}
                          />
                          <Button
                            size="small"
                            onClick={() =>
                              triggerSupportingDialog(offer.id, index)
                            }
                            loading={supportingUploadBusy[busyKey]}
                            style={{
                              borderColor: PRIMARY_COLOR,
                              color: PRIMARY_COLOR,
                            }}
                          >
                            {supportingUploadBusy[busyKey]
                              ? "Uploading..."
                              : "Upload"}
                          </Button>
                        </div>
                      </div>
                      {uploads.length > 0 && (
                        <div style={{ marginTop: "6px" }}>
                          {uploads.map((doc) => (
                            <a
                              key={doc.id}
                              href={doc.file_url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: "block",
                                fontSize: "12px",
                                color: PRIMARY_COLOR,
                              }}
                            >
                              {doc.file_name || "View document"}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {documentsFromLender.length > 0 && (
            <div style={{ marginTop: "12px" }}>
              <div style={{ fontSize: "12px", fontWeight: 600 }}>
                Documents from lender
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  marginTop: "6px",
                }}
              >
                {documentsFromLender.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.file_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: "13px", color: PRIMARY_COLOR }}
                  >
                    {doc.file_name || "View document"}
                    {doc.notes ? ` — ${doc.notes}` : ""}
                  </a>
                ))}
              </div>
            </div>
          )}

          {documentsFromBorrower.length > 0 && (
            <div style={{ marginTop: "12px" }}>
              <div style={{ fontSize: "12px", fontWeight: 600 }}>
                Your uploads
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  marginTop: "6px",
                }}
              >
                {documentsFromBorrower.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.file_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: "13px", color: PRIMARY_COLOR }}
                  >
                    {doc.file_name || "View document"}
                    {doc.notes ? ` — ${doc.notes}` : ""}
                  </a>
                ))}
              </div>
            </div>
          )}

          {canUpload && (
            <div
              style={{
                marginTop: "16px",
                background: "rgba(10,61,98,0.08)",
                padding: "14px",
                borderRadius: "var(--br-card-radius, 10px)",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: "13px" }}>
                Upload signed documents
              </div>
              <div style={{ fontSize: "12px", marginTop: "4px" }}>
                Review the lender's documents and upload your signed copies.
                We'll notify the lender immediately.
              </div>
              <Input.TextArea
                rows={2}
                placeholder="Optional note to your lender"
                value={noteValue}
                onChange={(event) =>
                  handleUploadNotesChange(offer.id, event.target.value)
                }
                style={{ marginTop: "10px" }}
                maxLength={400}
              />
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  icon={<UploadFileIcon fontSize="small" />}
                  onClick={() => triggerFileDialog(offer.id)}
                  loading={uploadingOfferId === offer.id}
                  style={PRIMARY_BUTTON_STYLE}
                >
                  {uploadingOfferId === offer.id
                    ? "Uploading..."
                    : "Select files"}
                </Button>
                <Button
                  type="default"
                  onClick={() => handleConfirmDocuments(offer)}
                  loading={confirmingOfferId === offer.id}
                  style={{ borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR }}
                >
                  {confirmingOfferId === offer.id
                    ? "Sending..."
                    : "Notify lender documents are ready"}
                </Button>
              </div>
              <input
                type="file"
                style={{ display: "none" }}
                multiple
                accept="application/pdf,image/*"
                ref={(element) => {
                  fileInputRefs.current[offer.id] = element;
                }}
                onChange={(event) => onFileChange(offer, event)}
              />
            </div>
          )}

          {canAct && (
            <div className="slActions">
              <Button
                className="slBtn slBtnPrimary"
                disabled={actionDisabled}
                onClick={() => openOfferModal("accept", offer)}
              >
                Accept offer
              </Button>

              <Button
                className="slBtn slBtnOutline"
                disabled={actionDisabled}
                onClick={() => openOfferModal("renegotiate", offer)}
              >
                Renegotiate
              </Button>

              <Button
                className="slBtn"
                danger
                disabled={actionDisabled}
                onClick={() => openOfferModal("reject", offer)}
              >
                Reject
              </Button>
            </div>
          )}

          {offer.next_repayment && (
            <div style={{ marginTop: "12px" }}>
              <div style={{ fontSize: "11px", opacity: 0.6 }}>
                Next repayment
              </div>
              <div style={{ fontWeight: 600 }}>
                {formatDate(offer.next_repayment.due_date)} ·{" "}
                {formatCurrency(offer.next_repayment.amount_due)}
              </div>
            </div>
          )}

          {repaymentSchedules.length > 0 && (
            <div style={{ marginTop: "12px" }}>
              <Button
                onClick={() => openRepaymentScheduleModal(offer)}
                style={{ borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR }}
              >
                Repayment schedule
              </Button>
            </div>
          )}

          {renderHistory(offer)}
        </div>
      </div>
    );
  };

  const getPillClassForLoanStatus = (status) => {
    if (!status) return "info";
    if (status.includes("rejected")) return "danger";
    if (status.includes("pending")) return "warning";
    if (status.includes("funded")) return "success";
    return "info";
  };

  const updateLoanVisibility = async (loanId, visible) => {
    setVisibilityUpdating((previous) => ({ ...previous, [loanId]: true }));
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/personalLoans/${loanId}/visibility`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({ allow_lender_visibility: visible }),
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to update loan visibility.");
      }
      setMyLoans((previous) =>
        previous.map((loan) =>
          loan.id === loanId
            ? { ...loan, allow_lender_visibility: json.data.allow_lender_visibility }
            : loan,
        ),
      );
      message.success(json.message);
    } catch (error) {
      message.error(error.message || "Unable to update loan visibility.");
    } finally {
      setVisibilityUpdating((previous) => ({ ...previous, [loanId]: false }));
    }
  };

  const renderLoanCard = (loan) => {
    const isWithdrawn = loan.allow_lender_visibility === false;
    const pillClass = getPillClassForLoanStatus(loan.status);

    return (
      <div
        key={loan.id}
        className={["slCard", isMobile ? "isMobile" : ""].join(" ")}
        style={{ color: PRIMARY_COLOR }}
      >
        <div className="slTopRow">
          <div className="slPills">
            <span className={`slPill ${pillClass}`}>
              {loan.status ? loan.status.replace(/_/g, " ") : "—"}
            </span>
            <span className="slPill">Money Lender Loan {loan.id}</span>
            {isWithdrawn && (
              <span className="slPill warning">Withdrawn from Market</span>
            )}
          </div>
        </div>

        <div className="slHeader">
          <div className="slAmountLabel">Requested amount</div>
          <div className="slAmountValue">{formatCurrency(loan.amount)}</div>
          <div className="slSubLine">
            Applied {formatDateTime(loan.created_at)}
          </div>
        </div>

        <div className="slMetaGrid">
          <div className="slMetaItem">
            <div className="slMetaK">Desired rate</div>
            <div className="slMetaV">
              {formatPercentPerMonth(
                loan.desired_rate_pa ?? loan.desired_rate_pa,
              )}
            </div>
          </div>
          <div className="slMetaItem">
            <div className="slMetaK">Desired tenor</div>
            <div className="slMetaV">
              {formatMonths(loan.desired_tenor_months)}
            </div>
          </div>
          <div className="slMetaItem" style={{ gridColumn: "1 / -1" }}>
            <div className="slMetaK">Status</div>
            <div className="slMetaV" style={{ textTransform: "capitalize" }}>
              {loan.status ? loan.status.replace(/_/g, " ") : "—"}
            </div>
          </div>
        </div>

        <div className="slActions">
          <Button
            className="slBtn slBtnPrimary"
            loading={!!visibilityUpdating[loan.id]}
            disabled={!!visibilityUpdating[loan.id]}
            onClick={() => updateLoanVisibility(loan.id, isWithdrawn)}
          >
            {isWithdrawn ? "Push to market" : "Withdraw from market"}
          </Button>
        </div>
        <div className="slMutedNote">
          {isWithdrawn
            ? "This request is hidden from the marketplace. You can push it back to market to receive new offers."
            : "Withdrawing hides this request from the marketplace. Existing offers and repayments remain unchanged."}
        </div>

        {/* {statementCoverageEnabled ? (
          <div className="slMutedNote">
            <div style={{ fontWeight: 850, marginBottom: 6 }}>
              Bank statement coverage (last 6 months)
            </div>

            {statementCoverageLoading[loan.id] ? (
              <div>Checking coverage...</div>
            ) : statementCoverageMap[loan.id]?.is_complete ? (
              <div>All required months are present.</div>
            ) : statementCoverageMap[loan.id]?.missing_months?.length ? (
              <>
                <div>
                  Missing:{" "}
                  {statementCoverageMap[loan.id].missing_months
                    .map((m) => m.label)
                    .join(", ")}
                </div>
                <div style={{ marginTop: 6, opacity: 0.8 }}>
                  If your documents are correct, you can disregard this notice.
                </div>
                <Button
                  className="slBtn slBtnOutline"
                  style={{ marginTop: 10 }}
                  onClick={() =>
                    openStatementCoverageModal(
                      loan,
                      statementCoverageMap[loan.id].missing_months,
                    )
                  }
                >
                  Add missing bank documents
                </Button>
              </>
            ) : (
              <div>No statement data available yet.</div>
            )}
          </div>
        ) : null} */}
      </div>
    );
  };

  const containerStyles = useMemo(
    () => ({
      minHeight: isLargeScreen ? "calc(100% - 80px)" : "calc(100vh - 80px)",
      width: "calc(100% - 40px)",
      marginLeft: isLargeScreen ? "20px" : "0px",
      borderRadius: isMobile ? "10px" : "10px",
      marginTop: "5px",
      gap: "5px",
      position: "relative",
      overflowY: "auto",
      color: PRIMARY_COLOR,
    }),
    [isLargeScreen, isMobile],
  );

  return (
    <Box m="4px 20px 20px 20px">
      <OfferReadyModal
        open={offerAlert.open}
        product="personal"
        onClose={closeOfferAlert}
        onViewOffer={viewOfferFromAlert}
      />
      <Modal
        open={repaymentScheduleModal.open}
        onCancel={closeRepaymentScheduleModal}
        footer={[
          <Button key="close" onClick={closeRepaymentScheduleModal}>
            Close
          </Button>,
        ]}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: 700,
            color: PRIMARY_COLOR,
            marginBottom: "12px",
          }}
        >
          Repayment Schedule
        </div>
        {(repaymentScheduleModal.offer?.repayment_schedules || []).length >
        0 ? (
          (repaymentScheduleModal.offer?.repayment_schedules || [])
            .slice()
            .sort(
              (a, b) =>
                new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
            )
            .map((schedule, index) => (
              <div
                key={schedule.id || index}
                style={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: "10px",
                  padding: "12px",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: "13px" }}>
                    Installment {index + 1}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color:
                        isRepaymentSettled(schedule) ? "#139F2ACC" : "#9C8A00",
                      background:
                        isRepaymentSettled(schedule)
                          ? "rgba(19,159,42,0.15)"
                          : "rgba(231,208,17,0.2)",
                      padding: "4px 10px",
                      borderRadius: "var(--br-card-radius, 20px)",
                      fontWeight: 600,
                    }}
                  >
                    {repaymentStatusLabel(schedule)}
                  </div>
                </div>
                <div style={{ fontSize: "12px", marginTop: "6px" }}>
                  Due {formatDate(schedule.due_date)}
                </div>
                <div style={{ fontWeight: 600, marginTop: "4px" }}>
                  {formatCurrency(schedule.amount_due)}
                </div>
              </div>
            ))
        ) : (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              fontSize: "13px",
              color: "rgba(0,0,0,0.5)",
            }}
          >
            No repayment schedule available.
          </div>
        )}
      </Modal>
      <Modal
        open={statementCoverageModal.open}
        onCancel={closeStatementCoverageModal}
        footer={[
          <Button key="cancel" onClick={closeStatementCoverageModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={statementCoverageSubmitting}
            onClick={submitStatementCoverageUploads}
          >
            Submit documents
          </Button>,
        ]}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: 700,
            color: PRIMARY_COLOR,
            marginBottom: "8px",
          }}
        >
          Add missing bank statements
        </div>
        <div style={{ fontSize: "12px", color: "rgba(10, 61, 98, 0.7)" }}>
          This check helps streamline your loan process. If the documents you
          already submitted are correct, you can disregard this request.
        </div>
        <div style={{ marginTop: "16px", display: "grid", gap: "12px" }}>
          {statementCoverageModal.missingMonths.length ? (
            statementCoverageModal.missingMonths.map((month) => (
              <div
                key={month.key}
                style={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: "10px",
                  padding: "12px",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: "13px" }}>
                  {month.label}
                </div>
                <input
                  type="file"
                  multiple
                  accept="application/pdf,image/*"
                  onChange={(event) =>
                    handleStatementCoverageUpload(month.key, event.target.files)
                  }
                  style={{ marginTop: "8px" }}
                />
              </div>
            ))
          ) : (
            <div style={{ fontSize: "12px", opacity: 0.7 }}>
              No missing months detected.
            </div>
          )}
        </div>
      </Modal>
      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="20px">
        <Box
          gridColumn={isMobile ? "span 12" : "span 9"}
          gridRow="span 2"
          className="loan-shell"
        >
          <div className="loan-header">
            <div className="loan-title">Money Lender Loan</div>
            <div className="loan-subtitle">
              Apply once. Get matched with the best licensed moneylenders in
              Singapore.
            </div>
          </div>

          <div
            style={{ height: "90vh", overflowY: "auto" }}
            className="loan-tabs"
          >
            <div className="loan-tabs-wrap">
              <Tabs
                className="loan-tabs"
                activeKey={activeTab}
                onChange={setActiveTab}
              >
                <TabPane tab="Apply Loan" key="apply">
                  <div
                    className="loan-card loan-hover-card"
                    style={{ padding: 18, width: "100%" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        width: "100%",
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <div
                          style={{
                            background:
                              "rgba(var(--br-primary-rgb, 96,169,247), 0.1)",
                            border:
                              "1px solid rgba(var(--br-primary-rgb, 67,121,178), 1)",
                            padding: "10px",
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            borderRadius: "var(--br-card-radius, 10px)",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              background:
                                "rgba(var(--br-primary-rgb, 0,76,158), 1)",
                              padding: "10px",
                              borderRadius: "var(--br-card-radius, 8px)",
                              width: "50px",
                              height: "50px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <StarBorderIcon
                              style={{ color: "white", fontSize: 28 }}
                            />
                          </div>
                          <div style={{ marginLeft: "15px" }}>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: !isMobile ? 18 : 14,
                                lineHeight: !isMobile ? "18px" : "14px",
                              }}
                            >
                              Matched with our exclusive, hand-picked lenders*
                            </div>
                            <div
                              style={{
                                marginTop: !isMobile ? "0px" : "4px",
                                fontSize: 12,
                              }}
                            >
                              MAS-Licensed · Highest Approval Rates · No Hidden
                              Fees
                            </div>
                          </div>
                          {!isMobile ? (
                            <div
                              style={{
                                position: "absolute",
                                right: "20px",
                                top: "50%",
                                transform: "translate(0, -50%)",
                                padding: "5px 15px",
                                borderRadius: "var(--br-card-radius, 30px)",
                                color:
                                  "rgba(var(--br-primary-rgb, 0,76,158), 1)",
                                background: "var(--br-surface, white)",
                                fontWeight: 700,
                                fontSize: 14,
                              }}
                            >
                              MAS Licensed Moneylenders *
                            </div>
                          ) : null}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "rgba(20,44,68,0.58)",
                            marginTop: "20px",
                          }}
                        >
                          * Moneylenders are picked based on ease of application
                          / approval + the service guarantee that they will
                          honour their in-principal approval on our platform
                          when users visit the branch.
                        </div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 900,
                            color: PRIMARY_COLOR,
                          }}
                        >
                          Apply for Money Lender loan
                        </div>
                        <div
                          style={{
                            marginTop: 1,
                            fontSize: 13,
                            fontWeight: 700,
                            color: "rgba(20,44,68,0.58)",
                          }}
                        >
                          Submit a request and let lenders start sending offers.
                        </div>
                      </div>

                      {/* this is the “Account Manager pop” vibe */}
                      {/* <div className="loan-chip">Fast approval flow</div> */}
                    </div>
                    {!isMobile ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "20px",
                          marginTop: "15px",
                        }}
                      >
                        <div
                          style={{
                            padding: "5px 15px",
                            borderRadius: "var(--br-card-radius, 20px)",
                            background: "rgba(96, 247, 157, 0.1)",
                            border: "1px solid rgba(67, 178, 139, 1)",
                            color: "rgba(0, 158, 87, 1)",
                          }}
                        >
                          ● 94% average approval rate
                        </div>
                        <div
                          style={{
                            padding: "5px 15px",
                            borderRadius: "var(--br-card-radius, 20px)",
                            background: "rgba(114, 96, 247, 0.1)",
                            border: "1px solid rgba(67, 67, 178, 1)",
                            color: "rgba(18, 0, 158, 1)",
                          }}
                        >
                          ● Offers within 1 hour
                        </div>
                        <div
                          style={{
                            padding: "5px 15px",
                            borderRadius: "var(--br-card-radius, 20px)",
                            background: "rgba(114, 96, 247, 0.1)",
                            border: "1px solid rgba(67, 67, 178, 1)",
                            color: "rgba(18, 0, 158, 1)",
                          }}
                        >
                          ● No impact on credit score
                        </div>
                      </div>
                    ) : null}

                    {!isMobile ? (
                      <div
                        style={{
                          background: "rgba(96, 247, 157, 0.1)",
                          border: "1px solid rgba(67, 178, 139, 1)",
                          padding: "10px",
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          borderRadius: "var(--br-card-radius, 10px)",
                          position: "relative",
                          marginTop: "20px",
                        }}
                      >
                        <div
                          style={{
                            background: "rgba(96, 247, 157, 0.1)",
                            color: "rgba(0, 158, 87, 1)",
                            padding: "10px",
                            borderRadius: "var(--br-card-radius, 8px)",
                            width: "50px",
                            height: "50px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <TaskAltIcon
                            style={{
                              color: "rgba(0, 158, 87, 1)",
                              fontSize: 28,
                            }}
                          />
                        </div>
                        <div style={{ marginLeft: "15px" }}>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: 18,
                              color: "rgba(0, 71, 39, 1)",
                            }}
                          >
                            Get the same offer at the branch — guaranteed
                          </div>
                          <div
                            style={{
                              marginTop: "-5px",
                              color: "rgba(0, 71, 39, 1)",
                            }}
                          >
                            90% of SmartLend users receive the same loan offer
                            when they visit the moneylender in person. No
                            surprises, no last-minute changes.
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {eligibilityLoading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          marginTop: 24,
                        }}
                      >
                        <CircularProgress size={28} />
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            marginTop: 20,
                            display: "grid",
                            gap: 14,
                            gridTemplateColumns: isMobile
                              ? "1fr"
                              : "repeat(4, minmax(170px, 1fr))",
                          }}
                        >
                          <div className="loan-input">
                            <div className="loan-field-label">Loan amount</div>
                            {loanCap ? (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 12,
                                  color: "rgba(20,44,68,0.6)",
                                }}
                              >
                                You can apply up to{" "}
                                {formatCurrency(applyMaxAmount)} based on{" "}
                                {loanCapDetails?.source || "income data"}.
                              </div>
                            ) : requiresPayslipUpload ? (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 12,
                                  color: "rgba(20,44,68,0.6)",
                                }}
                              >
                                We need your last 3 months of payslips to set
                                your maximum loan amount.
                              </div>
                            ) : null}
                            <div style={{ marginTop: 8 }}>
                              <InputNumber
                                min={1000}
                                value={applyForm.amount}
                                formatter={(value) =>
                                  `S$ ${value}`.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    ",",
                                  )
                                }
                                parser={(value) =>
                                  value.replace(/S\$\s?|(,*)/g, "")
                                }
                                onChange={(value) => {
                                  const numericValue = Number(value || 0);
                                  setLoanAmountExceeded(
                                    Boolean(
                                      loanCap && numericValue > applyMaxAmount,
                                    ),
                                  );
                                  setApplyForm((prev) => ({
                                    ...prev,
                                    amount: numericValue,
                                  }));
                                  if (!trackingRef.current.enteredLoanAmount) {
                                    sendTrackingUpdate({
                                      enteredLoanAmount: true,
                                      loanAmount: numericValue,
                                    });
                                    return;
                                  }
                                  sendTrackingUpdate({
                                    loanAmount: numericValue,
                                  });
                                }}
                              />
                              {loanCap && loanAmountExceeded ? (
                                <div
                                  style={{
                                    marginTop: 6,
                                    fontSize: 12,
                                    color: "#b42318",
                                  }}
                                >
                                  You cannot apply for more than{" "}
                                  {formatCurrency(applyMaxAmount)}.
                                </div>
                              ) : null}
                              {campaignAmountAutofilled ? (
                                <div
                                  style={{
                                    marginTop: 6,
                                    fontSize: 12,
                                    color: "rgba(20,44,68,0.65)",
                                  }}
                                >
                                  This amount was auto-filled based on the
                                  information you provided earlier. You can
                                  still reduce it to a lower amount if needed.
                                </div>
                              ) : null}
                            </div>
                          </div>

                          <div className="loan-input">
                            <div className="loan-field-label">
                              Commited Effective Int Rate. (% P.M.){" "}
                              <Tooltip title={COMMITTED_EFFECTIVE_RATE_TOOLTIP}>
                                <QuestionCircleOutlined
                                  style={{ color: "#8c8c8c", cursor: "help" }}
                                />
                              </Tooltip>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <Input
                                value={`${FIXED_MONEY_LENDER_INTEREST_RATE_PM.toFixed(2)}%`}
                                disabled
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>

                          <div className="loan-input">
                            <div className="loan-field-label">
                              Preferred tenor (months)
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <select
                                value={applyForm.desired_tenor_months}
                                onChange={(event) => {
                                  const value = Number(event.target.value);
                                  setApplyForm((prev) => ({
                                    ...prev,
                                    desired_tenor_months:
                                      normalizePreferredTenorMonths(value),
                                  }));
                                  if (!trackingRef.current.enteredTenor) {
                                    sendTrackingUpdate({ enteredTenor: true });
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  height: 32,
                                  borderRadius: 8,
                                  border: "1px solid #d9d9d9",
                                  padding: "0 10px",
                                  fontFamily: "inherit",
                                }}
                              >
                                {ALLOWED_TENOR_MONTH_OPTIONS.map((month) => (
                                  <option key={month} value={month}>
                                    {month} months
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="loan-input">
                            <div className="loan-field-label">
                              Processing fee
                            </div>
                            <div style={{ marginTop: 8, width: "100%" }}>
                              <Input
                                value="10%"
                                disabled
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            marginTop: 16,
                            borderRadius: "var(--br-card-radius, 14px)",
                            border: "1px solid rgba(185, 204, 255, 0.9)",
                            background: "#f4f7fd",
                            padding: isMobile ? "16px 14px" : "18px 20px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: isMobile ? "flex-start" : "center",
                            gap: 12,
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "rgba(109, 125, 155, 1)",
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                              }}
                            >
                              Estimated monthly repayment
                            </div>
                            <div
                              style={{
                                marginTop: 2,
                                fontSize: isMobile ? 26 : 35,
                                lineHeight: 1,
                                fontWeight: 800,
                                color: "rgba(26, 38, 65, 1)",
                              }}
                            >
                              {applyEstimatedMonthlyInstallment
                                ? formatRoundedCurrency(
                                    applyEstimatedMonthlyInstallment,
                                  )
                                : "S$0"}
                              <span
                                style={{
                                  marginLeft: 4,
                                  fontSize: 18,
                                  color: "rgba(109, 125, 155, 1)",
                                  fontWeight: 700,
                                }}
                              >
                                /month
                              </span>
                            </div>
                            <div
                              style={{
                                marginTop: 8,
                                fontSize: 12,
                                color: "rgba(132, 148, 177, 1)",
                              }}
                            >
                              Based on {applyMonthlyRate.toFixed(2)}% p.m. ·{" "}
                              {formatMonths(applyForm.desired_tenor_months)} ·{" "}
                              {formatRoundedCurrency(applyForm.amount)}
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: isMobile ? "flex-start" : "flex-end",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                borderRadius: 999,
                                padding: "6px 12px",
                                fontSize: 14,
                                fontWeight: 700,
                                background: isWithinMasMonthlyInterestLimit
                                  ? "rgba(16, 185, 129, 0.12)"
                                  : "rgba(245, 158, 11, 0.12)",
                                color: isWithinMasMonthlyInterestLimit
                                  ? "rgba(2, 132, 91, 1)"
                                  : "rgba(180, 83, 9, 1)",
                              }}
                            >
                              {isWithinMasMonthlyInterestLimit
                                ? "Fixed rate"
                                : "Outside allowed range"}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: "rgba(132, 148, 177, 1)",
                              }}
                            >
                              Interest rate is locked at 3.92% p.m.
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            marginTop: 18,
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                          }}
                        >
                          <Tooltip
                            title={
                              canApply
                                ? ""
                                : eligibilityMessage ||
                                  "You'll be able to submit a new request after refreshing your MyInfo data."
                            }
                          >
                            <div>
                              <Button
                                type="primary"
                                disabled={!canApply || applyBusy}
                                loading={applyBusy}
                                onClick={openApplyModal}
                                className="loan-primary-btn"
                                style={{
                                  ...PRIMARY_BUTTON_STYLE,
                                  maxWidth: isMobile ? "100%" : 240,
                                  width: isMobile ? "100%" : "auto",
                                  background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 120%)`,
                                }}
                              >
                                Submit request
                              </Button>
                            </div>
                          </Tooltip>

                          {!canApply ? (
                            <Button
                              onClick={() => history.push("/stg-b-profile")}
                              className="loan-primary-btn"
                              style={{
                                ...PRIMARY_BUTTON_STYLE,
                                maxWidth: isMobile ? "100%" : 240,
                                width: isMobile ? "100%" : "auto",
                                background: `transparent`,
                                color: BRAND,
                              }}
                            >
                              Retrieve MyInfo data
                            </Button>
                          ) : null}
                        </div>
                        <div style={{ marginTop: "15px" }}>
                          {!canApply && eligibilityMessage ? (
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: "rgba(187, 108, 34, 1)",
                                border: "1px solid rgba(187, 121, 34, 1)",
                                background: "rgba(255, 246, 239, 1)",
                                borderRadius: "var(--br-card-radius, 10px)",
                                padding: "5px 15px",
                              }}
                            >
                              {eligibilityMessage}
                            </div>
                          ) : null}
                        </div>
                        <div>
                          <div className="faq-section">
                            <div className="faq-header">
                              <div className="faq-eyebrow">Got questions?</div>
                              <div className="faq-title">
                                Frequently Asked Questions
                              </div>
                              <div className="faq-sub">
                                Real answers to what Singaporeans ask before
                                borrowing — including how SmartLend works for
                                you.
                              </div>
                            </div>

                            {/* Tabs / categories */}
                            <div className="faq-cats">
                              {CATS.map((c) => (
                                <button
                                  key={c.key}
                                  type="button"
                                  className={`faq-cat ${activeCat === c.key ? "active" : ""}`}
                                  onClick={() => onPickCat(c.key)}
                                >
                                  {c.label}
                                </button>
                              ))}
                            </div>

                            {/* Accordion list */}
                            <div className="faq-list">
                              {visibleFaqs.map((item, idx) => {
                                const key = `${activeCat}:${idx}`;
                                const isOpen = openKey === key;

                                return (
                                  <div
                                    key={key}
                                    className="faq-item"
                                    data-cat={item.cat}
                                  >
                                    <button
                                      type="button"
                                      className={`faq-q ${isOpen ? "open" : ""}`}
                                      onClick={() => toggle(idx)}
                                    >
                                      <span>{item.q}</span>
                                      <span
                                        className="faq-icon"
                                        aria-hidden="true"
                                      >
                                        +
                                      </span>
                                    </button>

                                    <div
                                      className={`faq-a ${isOpen ? "open" : ""}`}
                                    >
                                      {item.a}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            background: "#f5f8ff",
                            border: "1.5px solid #c5d8ff",
                            borderRadius: "var(--br-card-radius, 10px)",
                            padding: "12px 16px",
                            marginTop: 12,
                            fontSize: 13,
                            color: "#1a2236",
                            lineHeight: 1.6,
                          }}
                        >
                          <strong>Don&apos;t have Singpass?</strong> No worries.
                          If you&apos;re having trouble signing up or
                          you&apos;re a foreigner without Singpass access,
                          simply WhatsApp us and our team will assist you
                          manually.
                          <br />
                          <a
                            href="https://wa.me/6587853373"
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 7,
                              marginTop: 10,
                              background: "#25D366",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 13,
                              padding: "9px 18px",
                              borderRadius: 50,
                              textDecoration: "none",
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.139.564 4.144 1.546 5.877L0 24l6.293-1.521A11.934 11.934 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.896 0-3.67-.5-5.2-1.373l-.374-.217-3.876.937.979-3.77-.243-.389A9.953 9.953 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                            </svg>
                            WhatsApp +65 8785 3373
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                </TabPane>

                <TabPane tab="Requested" key="requested">
                  <div style={{ fontSize: "16px", fontWeight: 700 }}>
                    Requested Money Lender loans
                  </div>
                  <div
                    style={{ marginTop: "6px", fontSize: "13px", opacity: 0.7 }}
                  >
                    Track applications and the offers you need to review.
                  </div>
                  {loadingLoans ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "24px",
                      }}
                    >
                      <CircularProgress size={28} />
                    </div>
                  ) : myLoans.length ? (
                    <div className="loanCardsWrap" style={{ marginTop: 16 }}>
                      {myLoans.map(renderLoanCard)}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "32px 20px",
                        color: "rgba(10, 61, 98, 0.55)",
                        fontSize: "13px",
                      }}
                    >
                      <AccessTimeIcon
                        style={{
                          fontSize: 48,
                          color: "rgba(10, 61, 98, 0.25)",
                        }}
                      />
                      <div style={{ marginTop: "12px" }}>
                        You haven’t submitted any Money Lender loan applications
                        yet.
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: "20px",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Offers to review
                  </div>
                  {requestedOffers.length ? (
                    <div
                      style={{ ...LOAN_HUB_CARD_LAYOUT, marginTop: "16px" }}
                      className="loan-hover-card"
                    >
                      {requestedOffers.map((offer) => renderOfferCard(offer))}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        color: "rgba(10, 61, 98, 0.55)",
                        padding: "24px 20px",
                        fontSize: "13px",
                      }}
                    >
                      <AccessTimeIcon
                        style={{
                          fontSize: 40,
                          color: "rgba(10, 61, 98, 0.25)",
                        }}
                      />
                      <div style={{ marginTop: "10px" }}>
                        No offers to review right now.
                      </div>
                    </div>
                  )}
                </TabPane>
                <TabPane tab="Awaiting Response" key="awaiting">
                  <div style={{ fontSize: "16px", fontWeight: 700 }}>
                    Awaiting response from lender
                  </div>
                  <div
                    style={{ marginTop: "6px", fontSize: "13px", opacity: 0.7 }}
                  >
                    Offers that are in progress while the lender responds.
                  </div>
                  {awaitingLenderOffers.length ? (
                    <div className="loanCardsWrap" style={{ marginTop: 16 }}>
                      {awaitingLenderOffers.map((offer) =>
                        renderOfferCard(offer, { showLenderProfile: true }),
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        color: "rgba(10, 61, 98, 0.55)",
                        padding: "32px 20px",
                        fontSize: "13px",
                      }}
                    >
                      <AccessTimeIcon
                        style={{
                          fontSize: 40,
                          color: "rgba(10, 61, 98, 0.25)",
                        }}
                      />
                      <div style={{ marginTop: "10px" }}>
                        No lender responses pending right now.
                      </div>
                    </div>
                  )}
                </TabPane>
                <TabPane tab="Ongoing Loans" key="ongoing">
                  <div style={{ fontSize: "16px", fontWeight: 700 }}>
                    Ongoing Money Lender loans
                  </div>
                  <div
                    style={{ marginTop: "6px", fontSize: "13px", opacity: 0.7 }}
                  >
                    Active loans that are being disbursed or already funded.
                  </div>
                  {ongoingOffers.length ? (
                    <div className="loanCardsWrap" style={{ marginTop: 16 }}>
                      {ongoingOffers.map((offer) => renderOfferCard(offer))}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        color: "rgba(10, 61, 98, 0.55)",
                        padding: "32px 20px",
                        fontSize: "13px",
                      }}
                    >
                      <CheckCircleOutlineIcon
                        style={{
                          fontSize: 40,
                          color: "rgba(10, 61, 98, 0.25)",
                        }}
                      />
                      <div style={{ marginTop: "10px" }}>
                        You have no ongoing Money Lender loans yet.
                      </div>
                    </div>
                  )}
                </TabPane>
              </Tabs>
            </div>
          </div>
        </Box>
        {!isMobile ? (
          <Box
            gridColumn="span 3"
            gridRow="span 2"
            backgroundColor="rgba(153, 178, 205, 0)"
            overflow="auto"
            minHeight="90.5vh"
          >
            <Box
              style={{
                border: "1px solid rgba(0, 0, 0, 0.05)",
                borderRadius: "10px",
                boxShadow: "var(--br-shadow, 0px 0px 10px rgba(0,0,0,0.05))",
              }}
              gridColumn="span 2"
              opacity="1.00"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "100%",
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <Box className="loan-card" style={{ padding: 12 }}>
                  {adImage ? (
                    <img
                      src={adImage}
                      alt="Advertisement"
                      onClick={(event) => {
                        event.preventDefault();
                        if (adLink) {
                          let url = adLink;

                          if (!/^https?:\/\//i.test(adLink)) {
                            url = `https://${adLink}`;
                          }

                          window.open(url, "_blank");
                        }
                      }}
                      style={{
                        position: "absolute",
                        top: "0",
                        left: "0",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "10px",
                        cursor: adLink ? "pointer" : "default",
                      }}
                    />
                  ) : null}
                </Box>
              </div>
            </Box>
          </Box>
        ) : null}
      </Box>

      <Modal
        width={isMobile ? "80vw" : "400px"}
        style={{ backgroundColor: "rgba(0, 0, 0, 0)" }}
        open={lenderProfileData ? true : false}
        onCancel={() => {
          setLenderProfileData(null);
        }}
        footer={null}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "15px",
            }}
          >
            <div>
              <img
                style={{
                  height: "80px",
                  width: "80px",
                  borderRadius: "50%",
                  background: "lightgrey",
                  boxShadow: "var(--br-shadow, 0px 0px 10px rgba(0,0,0,0.05))",
                  objectFit: "cover",
                }}
                src={lenderProfileData?.profile?.profile_photo}
                alt={lenderProfileData?.profile?.company_name || "Lender"}
              />
            </div>
            <div style={{ width: "100%" }}>
              <div
                style={{
                  color: "var(--br-primary, rgb(14, 90, 165))",
                  fontSize: "20px",
                  fontFamily: "Manrope,sans-serif",
                  fontWeight: 800,
                  marginBottom: "5px",
                }}
              >
                {lenderProfileData?.profile?.company_name}
              </div>
              {lenderProfileData?.profile?.additionals?.map((addi, index) => (
                <div key={index} style={{ display: "flex", gap: "10px" }}>
                  <div
                    style={{
                      width: "100%",
                      border: "none",
                      outline: "none",
                      color: "rgba(0,0,0,0.6)",
                      marginTop: "-5px",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {addi?.text}
                  </div>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginLeft: "-5px",
                  marginTop: "5px",
                }}
              >
                {lenderProfileData?.profile?.social?.map((soci, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "5px",
                      position: "relative",
                      cursor: "pointer",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={iconLibrary[soci?.icon]}
                      style={{
                        fontSize: 20,
                        color: "var(--br-primary, rgb(14, 90, 165))",
                      }}
                      onClick={() => {
                        if (soci.link !== "" && soci.link !== null) {
                          window.location.href = soci.link;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <hr
            style={{
              width: "90%",
              marginLeft: "5%",
              background: "grey",
              border: "none",
              outline: "none",
            }}
          />
          {lenderProfileData?.content?.map((cont, index) =>
            cont.type === "text" ? (
              <div
                key={index}
                style={{
                  position: "relative",
                  marginTop: cont.mt,
                  marginBottom: cont.mb,
                  textDecoration: cont.underline ? "underline" : "none",
                  fontWeight: cont.fontWeight === 400 ? 400 : 600,
                  fontSize: cont.fontWeight === 400 ? "12px" : "16px",
                  border: "none",
                  outline: "none",
                  color: "rgba(0,0,0,0.6)",
                  width: "100%",
                }}
              >
                {cont?.text}
              </div>
            ) : cont?.type === "button" ? (
              <div
                key={index}
                style={{
                  position: "relative",
                  display: "flex",
                  cursor: "pointer",
                }}
                onClick={() => {
                  if (cont.url !== "" && cont.url !== null) {
                    window.location.href = cont.url;
                  }
                }}
              >
                <div
                  style={{
                    marginTop: cont.mt,
                    marginBottom: cont.mb,
                    fontWeight: 500,
                    fontSize: "14px",
                    border: "1px solid rgba(0,0,0,0.05)",
                    outline: "none",
                    color: "white",
                    background: "var(--br-primary, rgb(14, 90, 165))",
                    padding: "7px 30px",
                    borderRadius: "var(--br-card-radius, 30px)",
                  }}
                >
                  {cont?.text}
                </div>
              </div>
            ) : (
              <div
                key={index}
                style={{
                  position: "relative",
                  marginTop: cont.mt,
                  marginBottom: cont.mb,
                }}
              >
                <img
                  style={{
                    objectFit: "cover",
                    width: "80%",
                    height: "150px",
                    border: "none",
                    outline: "none",
                  }}
                  src={cont?.url}
                />
              </div>
            ),
          )}
        </div>
      </Modal>

      <Modal
        open={applyModalOpen}
        onCancel={closeApplyModal}
        footer={null}
        width={isMobile ? "100%" : 720}
        closable={false}
        styles={{ body: { padding: 0 }, content: { padding: 0 } }}
        className="no-padding-modal"
      >
        <div style={{ overflow: "hidden", borderRadius: "15px" }}>
          <div
            style={{
              background: "linear-gradient(180deg, #0d2a5e 0%, #0b2045 100%)",
              color: "#fff",
              padding: isMobile ? "18px 16px" : "22px 28px",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                border: "1px solid rgba(108,177,255,0.4)",
                background: "rgba(47,112,186,0.3)",
                borderRadius: "999px",
                color: "var(--br-soft, #7fc2ff)",
                fontSize: "8px",
                fontWeight: 700,
                letterSpacing: "1.6px",
                textTransform: "uppercase",
                padding: "7px 14px",
              }}
            >
              <span role="img" aria-label="checklist">
                ✅
              </span>
              Application review
            </div>
            <Button
              type="text"
              onClick={closeApplyModal}
              icon={<CloseOutlined />}
              style={{
                position: "absolute",
                right: isMobile ? "16px" : "24px",
                top: isMobile ? "16px" : "20px",
                color: "rgba(255,255,255,0.9)",
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
              }}
            />
            <div
              style={{
                marginTop: "14px",
                fontSize: isMobile ? "26px" : "30px",
                fontWeight: 700,
                lineHeight: isMobile ? 1.1 : 1,
              }}
            >
              Confirm loan request
            </div>
            <div
              style={{
                marginTop: "12px",
                fontSize: "12px",
                lineHeight: isMobile ? 1.3 : 1.25,
                color: "rgba(238,245,255,0.92)",
                maxWidth: "95%",
              }}
            >
              Review your details and documents before sending your request to
              lenders.
            </div>
          </div>

          <div
            style={{
              padding: isMobile ? "18px 16px 16px" : "24px",
              background: "var(--br-surface, #fff)",
            }}
          >
            <div>
              <Steps
                current={applyStep}
                items={applySteps.map((step) => ({ title: step.title }))}
              />
            </div>
            <div style={{ marginTop: "20px" }}>
              {currentApplyStep === "contact" && (
                <div style={{ display: "grid", gap: "16px" }}>
                  <div
                    style={{ fontSize: "13px", color: "rgba(20,44,68,0.7)" }}
                  >
                    Please confirm your contact details. You must provide both
                    email and phone number before proceeding.
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", opacity: 0.7 }}>Email</div>
                    <Input
                      value={contactEmail}
                      onChange={(event) => setContactEmail(event.target.value)}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", opacity: 0.7 }}>
                      Phone number
                    </div>
                    <Input
                      value={contactPhone}
                      onChange={(event) => setContactPhone(event.target.value)}
                      placeholder="+65"
                    />
                  </div>
                </div>
              )}
              {currentApplyStep === "payslips" && (
                <div style={{ display: "grid", gap: "14px" }}>
                  <div
                    style={{ fontSize: "13px", color: "rgba(20,44,68,0.7)" }}
                  >
                    Upload your last 3 months of payslips. You can add multiple
                    files, and the upload is required before submitting.
                  </div>
                  <Upload
                    accept="application/pdf,image/png,image/jpeg"
                    multiple
                    fileList={payslipFileList}
                    beforeUpload={(file, fileList) => {
                      setPayslipFiles(fileList);
                      return false;
                    }}
                    onRemove={(file) => {
                      setPayslipFiles((prev) =>
                        prev.filter((entry) => entry.uid !== file.uid),
                      );
                    }}
                  >
                    <Button icon={<UploadOutlined />}>
                      Upload payslip files
                    </Button>
                  </Upload>
                </div>
              )}
              {currentApplyStep === "cbs" && (
                <div style={{ display: "grid", gap: "14px" }}>
                  <div
                    style={{ fontSize: "13px", color: "rgba(20,44,68,0.7)" }}
                  >
                    Retrieve your CBS report using the promo code below, then
                    upload the report so you can review the extracted details.
                  </div>
                  <div
                    style={{
                      border:
                        "1px solid rgba(var(--br-primary-rgb, 14,90,165), 0.16)",
                      borderRadius: "var(--br-card-radius, 12px)",
                      padding: "12px",
                      background: "rgba(245, 249, 255, 0.72)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "1.1px",
                        color: "rgba(10,61,98,0.62)",
                        fontWeight: 700,
                      }}
                    >
                      Promo code
                    </div>
                    <div
                      style={{
                        marginTop: "6px",
                        display: "inline-flex",
                        padding: "8px 14px",
                        borderRadius: "var(--br-card-radius, 20px)",
                        background:
                          "rgba(var(--br-primary-rgb, 14,90,165), 0.1)",
                        fontWeight: 700,
                      }}
                    >
                      {cbsPromoCode || "Promo code unavailable"}
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: "2px" }}>
                    {[
                      {
                        title: "Visit CBS Portal",
                        desc: "Go to the official CBS website",
                        action: true,
                      },
                      {
                        title: "Log in with Singpass",
                        desc: "Authenticate securely using your Singpass app or ID & password. No account creation required.",
                      },
                      {
                        title: "Paste the Promo Code",
                        desc: "Enter the promo code provided above to retrieve your complimentary CBS report.",
                      },
                      {
                        title: "Download Your CBS Report",
                        desc: 'Once generated, click "SAVE" to download your CBS report as a PDF file.',
                      },
                      {
                        title: "Upload Your CBS Report",
                        desc: "Save the file to your device and upload it below.",
                      },
                    ].map((step, index, arr) => (
                      <div
                        key={step.title}
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "stretch",
                        }}
                      >
                        <div
                          style={{
                            width: "26px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              width: "26px",
                              height: "26px",
                              borderRadius: "50%",
                              background: "#174d93",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "13px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {index + 1}
                          </div>
                          {index < arr.length - 1 ? (
                            <div
                              style={{
                                width: "2px",
                                flex: 1,
                                background: "rgba(23,77,147,0.24)",
                                marginTop: "6px",
                              }}
                            />
                          ) : null}
                        </div>
                        <div
                          style={{
                            paddingBottom: index < arr.length - 1 ? "12px" : 0,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "17px",
                              fontWeight: 700,
                              color: "#15213e",
                              lineHeight: 1.2,
                            }}
                          >
                            {step.title}
                          </div>
                          <div
                            style={{
                              marginTop: "4px",
                              fontSize: "13px",
                              color: "rgba(20,44,68,0.75)",
                              maxWidth: "600px",
                            }}
                          >
                            {step.desc}
                          </div>
                          {step.action ? (
                            <div
                              style={{
                                marginTop: "10px",
                                display: "inline-flex",
                                borderRadius: "10px",
                                overflow: "hidden",
                                border: "1px solid rgba(24,42,78,0.16)",
                              }}
                            >
                              <Button
                                type="text"
                                style={{
                                  borderRadius: 0,
                                  height: "44px",
                                  fontWeight: 700,
                                  color: "#1a325f",
                                  background: "#f7fafc",
                                }}
                                onClick={() =>
                                  window.open(
                                    "https://www.creditbureau.com.sg/SmartLend_Financial.html",
                                    "_blank",
                                  )
                                }
                              >
                                🌐 Get CBS Report
                              </Button>
                              <Button
                                type="text"
                                style={{
                                  borderLeft: "1px solid rgba(24,42,78,0.16)",
                                  borderRadius: 0,
                                  height: "44px",
                                  fontWeight: 700,
                                  color: "#1a325f",
                                  background: "var(--br-surface, #fff)",
                                }}
                                onClick={() => {
                                  if (!cbsPromoCode) return;
                                  navigator.clipboard.writeText(cbsPromoCode);
                                  message.success("Promo code copied.");
                                }}
                              >
                                Copy
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Upload
                    fileList={cbsFileList}
                    beforeUpload={handleCbsBeforeUpload}
                    onRemove={() => {
                      setCbsFiles([]);
                      setCbsFileText("");
                      setCbsExtractedData(null);
                      setCbsEnquiryDate(null);
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Upload CBS Report</Button>
                  </Upload>
                  {cbsExtractedData ? (
                    <div
                      style={{
                        border:
                          "1px solid rgba(var(--br-primary-rgb, 14,90,165), 0.2)",
                        borderRadius: "12px",
                        padding: "12px",
                        display: "grid",
                        gap: "6px",
                        fontSize: "12px",
                        color: "var(--br-text, rgba(20,44,68,0.85))",
                      }}
                    >
                      <div>Enquiry date: {cbsEnquiryDate || "—"}</div>
                      <div>
                        Risk grade: {cbsExtractedData.risk_grade || "—"}
                      </div>
                      <div>
                        Probability of default:{" "}
                        {cbsExtractedData.probability_default || "—"}
                      </div>
                      <div>
                        Secured credit limit:{" "}
                        {cbsExtractedData.secured_credit_limit || "—"}
                      </div>
                      <div>
                        Unsecured credit limit:{" "}
                        {cbsExtractedData.unsecured_credit_limit || "—"}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
              {currentApplyStep === "authorization" && (
                <div style={{ display: "grid", gap: "12px" }}>
                  <div
                    style={{ fontSize: "13px", color: "rgba(20,44,68,0.7)" }}
                  >
                    Review your submission details and confirm before submitting
                    the request.
                  </div>
                  <div
                    style={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: "10px",
                      padding: "12px",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>
                      Contact details
                    </div>
                    <div>Email: {contactEmail || "—"}</div>
                    <div>Phone: {contactPhone || "—"}</div>
                  </div>
                  {requiresPayslipUpload ? (
                    <div
                      style={{
                        border: "1px solid rgba(0,0,0,0.08)",
                        borderRadius: "10px",
                        padding: "12px",
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: "6px" }}>
                        Payslips
                      </div>
                      {payslipFiles.length ? (
                        payslipFiles.map((file) => (
                          <div key={file.uid || file.name}>{file.name}</div>
                        ))
                      ) : (
                        <div>No payslip files uploaded.</div>
                      )}
                    </div>
                  ) : null}
                  <div
                    style={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: "10px",
                      padding: "12px",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>
                      CBS report
                    </div>
                    <div>
                      {cbsFiles.length ? cbsFiles[0].name : "No file uploaded"}
                    </div>
                  </div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={authorizationChecked}
                      onChange={(event) =>
                        setAuthorizationChecked(event.target.checked)
                      }
                    />
                    I confirm the information and documents provided are
                    accurate.
                  </label>
                </div>
              )}
              {currentApplyStep === "visibility" && (
                <div style={{ display: "grid", gap: "12px" }}>
                  <div
                    style={{ fontSize: "13px", color: "rgba(20,44,68,0.7)" }}
                  >
                    Would you like to allow money-lenders to see your request?
                    Allowing this can help you receive more offers to compare.
                  </div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allowLenderVisibility}
                      onChange={(event) =>
                        setAllowLenderVisibility(event.target.checked)
                      }
                    />
                    Yes, share my request with money-lenders.
                  </label>
                </div>
              )}
            </div>
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button
                type="default"
                onClick={
                  applyStep === 0 ? closeApplyModal : handlePreviousApplyStep
                }
              >
                {applyStep === 0 ? "Cancel" : "Back"}
              </Button>
              {applyStep < applySteps.length - 1 ? (
                <Button
                  type="primary"
                  onClick={handleNextApplyStep}
                  disabled={nextDisabled}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleApplySubmit}
                  loading={applyBusy}
                  disabled={!canSubmitApply}
                >
                  Submit request
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={mlcbModalOpen}
        onCancel={closeMlcbModal}
        footer={null}
        width={isMobile ? "100%" : "40vw"}
        closable={false}
        styles={{ body: { padding: 0 }, content: { padding: 0 } }}
        className="no-padding-modal"
      >
        <div style={{ overflow: "hidden", borderRadius: "15px" }}>
          <div
            style={{
              background: "linear-gradient(180deg, #0d2a5e 0%, #0b2045 100%)",
              color: "#fff",
              padding: isMobile ? "18px 16px" : "22px 28px",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                border: "1px solid rgba(108,177,255,0.4)",
                background: "rgba(47,112,186,0.3)",
                borderRadius: "999px",
                color: "var(--br-soft, #7fc2ff)",
                fontSize: "8px",
                fontWeight: 700,
                letterSpacing: "1.6px",
                textTransform: "uppercase",
                padding: "7px 14px",
              }}
            >
              <span role="img" aria-label="document">
                📄
              </span>
              Required document
            </div>
            <Button
              type="text"
              onClick={closeMlcbModal}
              icon={<CloseOutlined />}
              style={{
                position: "absolute",
                right: isMobile ? "16px" : "24px",
                top: isMobile ? "16px" : "20px",
                color: "rgba(255,255,255,0.9)",
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
              }}
            />
            <div
              style={{
                marginTop: "14px",
                fontSize: isMobile ? "26px" : "30px",
                fontWeight: 700,
                lineHeight: isMobile ? 1.1 : 1,
              }}
            >
              Upload your MLCB Report
            </div>
            <div
              style={{
                marginTop: "12px",
                fontSize: isMobile ? "12px" : "12px",
                lineHeight: isMobile ? 1.3 : 1.25,
                color: "rgba(238,245,255,0.92)",
                maxWidth: "95%",
              }}
            >
              You are one step away from getting more offers. Your loan request
              is in market, but certain lenders require your MLCB report to
              proceed.
            </div>
          </div>

          <div style={{ padding: isMobile ? "16px" : "20px 24px 24px" }}>
            <div
              style={{
                border: "2px solid #b9d6f7",
                background: "#eef5ff",
                borderRadius: "var(--br-card-radius, 16px)",
                padding: isMobile ? "14px" : "16px",
                display: "flex",
                gap: "12px",
              }}
            >
              <BulbOutlined
                style={{ color: "#f2be42", fontSize: "16px", marginTop: "2px" }}
              />
              <div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#103670",
                  }}
                >
                  Why is the MLCB report needed?
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    color: "#23406a",
                    fontSize: "12px",
                    lineHeight: 1.45,
                  }}
                >
                  Lenders use your MLCB report to see your current loan
                  exposure. This helps them make a more informed decision — so
                  the offer you receive will be the most accurate and
                  competitive possible.
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: "20px",
                fontSize: "20px",
                fontWeight: 700,
                color: "#151a2d",
                lineHeight: 1.2,
              }}
            >
              How to get your MLCB report
            </div>

            {[
              {
                title: "Visit the MLCB Portal",
                description:
                  "Go to the official Moneylenders Credit Bureau website to access your report.",
              },
              {
                title: "Log in with Singpass",
                description:
                  "Use your Singpass app or ID & password to authenticate securely. No account creation needed.",
              },
              {
                title: "Request your credit report",
                description:
                  'Select "My Credit Report" and request a copy. The report is free for the first copy each year.',
              },
              {
                title: "Download as PDF & upload here",
                description:
                  "Save the report as a PDF file, then upload it below. Takes less than 2 minutes.",
              },
            ].map((step, index, steps) => (
              <div
                key={step.title}
                style={{ display: "flex", gap: "14px", marginTop: "16px" }}
              >
                <div
                  style={{
                    width: "42px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "#123c7d",
                      color: "#fff",
                      fontSize: "17px",
                      fontWeight: 700,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      style={{
                        width: "3px",
                        borderRadius: "3px",
                        background: "#d9e0eb",
                        minHeight: "20px",
                        marginTop: "7px",
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      color: "#1a2240",
                      fontSize: "16px",
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    {step.title}
                  </div>
                  <div
                    style={{
                      marginTop: "6px",
                      color: "#656b78",
                      fontSize: "10px",
                      lineHeight: 1.45,
                    }}
                  >
                    {step.description}
                  </div>
                  {index === 0 && (
                    <div style={{ display: "flex" }}>
                      <Button
                        style={{
                          marginTop: "12px",
                          borderRadius: "10px",
                          borderColor: "#a9ccf1",
                          color: "#1965c2",
                          fontWeight: 700,
                          fontSize: "15px",
                          height: "40px",
                          paddingInline: "14px",
                          alignItems: "center",
                          display: "flex",
                        }}
                        icon={<GlobalOutlined />}
                        href="https://mlcb.com.sg"
                        target="_blank"
                      >
                        mlcb.com.sg <LinkOutlined />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            <Upload
              accept="application/pdf,image/png,image/jpeg"
              maxCount={1}
              fileList={mlcbFileList}
              beforeUpload={handleMlcbBeforeUpload}
              onRemove={() => {
                setMlcbFiles([]);
              }}
            >
              <div
                style={{
                  marginTop: "16px",
                  border: "2px dashed #b5d2ef",
                  background: "#f4f8fd",
                  borderRadius: "var(--br-card-radius, 16px)",
                  padding: isMobile ? "20px 12px" : "24px",
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: isMobile ? "32px" : "40px" }}>📥</div>
                <div
                  style={{
                    marginTop: "10px",
                    fontSize: "17px",
                    fontWeight: 700,
                    color: "#1d2038",
                  }}
                >
                  Drop your MLCB report here
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "15px",
                    color: "#7b8088",
                  }}
                >
                  or{" "}
                  <span style={{ color: "#1f6ecd", fontWeight: 700 }}>
                    browse to upload
                  </span>{" "}
                  from your device
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "#a1a6ad",
                  }}
                >
                  Accepted formats: PDF · JPG · PNG · Max 50MB
                </div>
              </div>
            </Upload>

            <div
              style={{
                marginTop: "20px",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1.65fr 1.1fr",
                gap: "10px",
              }}
            >
              <Button
                type="default"
                onClick={closeMlcbModal}
                style={{
                  height: "50px",
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "16px",
                }}
              >
                Maybe later
              </Button>
              <Upload
                accept="application/pdf,image/png,image/jpeg"
                maxCount={1}
                showUploadList={false}
                beforeUpload={handleMlcbBeforeUpload}
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  style={{
                    width: "100%",
                    height: "50px",
                    borderRadius: "var(--br-card-radius, 12px)",
                    fontWeight: 700,
                    fontSize: "16px",
                    background: "linear-gradient(90deg, #1e6fd0, #1656b0)",
                    borderColor: "#1a63bf",
                  }}
                >
                  Upload MLCB Report
                </Button>
              </Upload>
              <Button
                type="primary"
                onClick={handleMlcbUploadSubmit}
                loading={mlcbUploadBusy}
                disabled={!mlcbFiles.length}
                style={{
                  height: "50px",
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "16px",
                }}
              >
                Submit report
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={offerAction.open}
        onCancel={closeOfferModal}
        onOk={submitOfferAction}
        confirmLoading={offerSubmitting}
        okText={
          offerAction.action === "accept"
            ? "Accept offer"
            : offerAction.action === "reject"
              ? "Reject offer"
              : "Send request"
        }
        title={
          offerAction.action === "accept"
            ? "Accept lender offer"
            : offerAction.action === "reject"
              ? "Reject lender offer"
              : "Request adjustments"
        }
        okButtonProps={{ style: PRIMARY_BUTTON_STYLE }}
        cancelButtonProps={{
          style: { borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR },
        }}
      >
        <div
          style={{
            fontSize: "13px",
            color: "var(--br-text, rgba(20,44,68,0.8))",
          }}
        >
          {offerAction.action === "accept" &&
            "Accepting this offer tells the lender to proceed with document preparation."}
          {offerAction.action === "reject" &&
            "Let the lender know if you'd prefer to withdraw from this offer."}
          {offerAction.action === "renegotiate" &&
            "Share what you'd like the lender to adjust so they can revise their offer."}
        </div>
        {offerAction.action === "accept" && (
          <div style={{ marginTop: "12px", display: "grid", gap: "6px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600 }}>
              Accepted amount
            </div>
            <InputNumber
              min={1}
              max={acceptOfferMaximum || undefined}
              value={acceptOfferAmount}
              onChange={(value) => setAcceptOfferAmount(value)}
              style={{ width: "100%" }}
            />
            {acceptOfferMaximum ? (
              <div style={{ fontSize: "11px", color: "rgba(20,44,68,0.6)" }}>
                Maximum available: {formatCurrency(acceptOfferMaximum)}
              </div>
            ) : null}
            {acceptOfferMonthlyInstallment !== null ? (
              <div style={{ fontSize: "12px", fontWeight: 600 }}>
                Estimated monthly instalment:{" "}
                {formatCurrency(acceptOfferMonthlyInstallment)}
              </div>
            ) : (
              <div style={{ fontSize: "11px", color: "rgba(20,44,68,0.6)" }}>
                Enter an amount to see the estimated monthly instalment.
              </div>
            )}
          </div>
        )}
        {offerAction.action === "accept" &&
        acceptOfferAdjustment?.isAdjusted ? (
          <div
            style={{
              marginTop: "10px",
              padding: "10px 12px",
              borderRadius: "var(--br-card-radius, 10px)",
              background: "rgba(var(--br-primary-rgb, 14,90,165), 0.08)",
              color: "var(--br-text, rgba(20,44,68,0.8))",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            You are accepting{" "}
            <strong>
              {formatCurrency(acceptOfferAdjustment.adjustedAmount)}
            </strong>
            . This is an adjusted figure because you already accepted{" "}
            <strong>
              {formatCurrency(acceptOfferAdjustment.acceptedAmount)}
            </strong>{" "}
            from this loan request.
          </div>
        ) : null}
        {offerAction.action === "accept" &&
        acceptOfferAmount &&
        acceptOfferMaximum &&
        acceptOfferAmount < acceptOfferMaximum ? (
          <div
            style={{
              marginTop: "10px",
              padding: "10px 12px",
              borderRadius: "var(--br-card-radius, 10px)",
              background: "rgba(var(--br-primary-rgb, 14,90,165), 0.08)",
              color: "var(--br-text, rgba(20,44,68,0.8))",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            You are choosing to accept{" "}
            <strong>{formatCurrency(acceptOfferAmount)}</strong> instead of the
            maximum available{" "}
            <strong>{formatCurrency(acceptOfferMaximum)}</strong>.
          </div>
        ) : null}
        <Input.TextArea
          rows={3}
          style={{ marginTop: "12px" }}
          placeholder={
            offerAction.action === "renegotiate"
              ? "E.g. Could you consider a lower rate or longer tenor?"
              : "Optional message to the lender"
          }
          value={offerMessage}
          onChange={(event) => setOfferMessage(event.target.value)}
          maxLength={600}
        />
      </Modal>
    </Box>
  );
};

export default PersonalLoans;
